<template>
  <div :class="['page', { 'number-keyboard-open': numberKeyboardVisible }]">
    <button v-if="returnTo && !isEmbeddedFrame" type="button" class="context-back" @click="returnToSource">
      <Icon type="ios-arrow-back" />
      <span>{{ returnLabel }}</span>
    </button>

    <Card v-if="loading" dis-hover class="panel panel--loading">
      <div class="auth-loading">页面初始化中...</div>
    </Card>

    <Card v-if="!loading && authBlocked" dis-hover class="panel panel--error">
      <div class="auth-error">{{ authErrorMessage || '页面加载失败' }}</div>
    </Card>

    <template v-else-if="!loading">
      <div class="survey-progress">
        <div :class="['survey-progress__step', { active: currentStep >= 0 }]">
          <span>1</span><b>基本信息</b>
        </div>
        <i></i>
        <div :class="['survey-progress__step', { active: currentStep >= 1 }]">
          <span>2</span><b>健康信息</b>
        </div>
      </div>
      <Card v-show="currentStep === 0" dis-hover class="panel">
        <p slot="title">基本信息</p>
        <Form label-position="top">
          <FormItem ref="checkIdField" label="检查单号">
            <Input
              v-if="!isWechatBrowser"
              v-model.trim="code"
              :disabled="hasLockedCheckId"
              maxlength="200"
              placeholder="请输入检查单二维码链接"
              @on-change="syncManualCheckId"
            />
            <div
              v-else
              :class="['mobile-trigger', 'mobile-trigger--readonly', { 'mobile-trigger--disabled': hasLockedCheckId }]"
              @click="scanByWechat"
            >
            <span :class="scanResultText ? 'mobile-trigger__value' : 'mobile-trigger__placeholder'">
              {{ scanResultText || '点击扫描检查单二维码' }}
            </span>
              <Icon :type="hasLockedCheckId ? 'ios-lock-outline' : 'ios-qr-scanner'" color="#9ea7b4" />
            </div>
          </FormItem>
          <FormItem ref="trueNameField" label="姓名">
            <Input v-model="form.trueName" maxlength="20" placeholder="请输入姓名" />
          </FormItem>
          <div class="grid">
            <FormItem ref="sexField" label="性别">
              <div class="mobile-trigger" @click="showSexPicker = true">
              <span :class="sexDisplayText ? 'mobile-trigger__value' : 'mobile-trigger__placeholder'">
                {{ sexDisplayText || '请选择性别' }}
              </span>
                <Icon type="ios-arrow-forward" color="#9ea7b4" />
              </div>
            </FormItem>
            <FormItem ref="ageField" label="年龄" :class="numberFieldClasses('age')">
              <Input
                :value="form.age"
                readonly
                maxlength="3"
                placeholder="请输入年龄"
                @on-focus="openNumberKeyboard('age', 'ageField')"
              />
              <div v-if="numberFieldError('age')" class="number-field-error-text">{{ numberFieldError('age') }}</div>
            </FormItem>
          </div>
          <div class="grid">
            <FormItem ref="birthField" label="出生日期">
              <div class="mobile-trigger" @click="showBirthPicker = true">
              <span :class="form.birth ? 'mobile-trigger__value' : 'mobile-trigger__placeholder'">
                {{ form.birth || '请选择出生日期' }}
              </span>
                <Icon type="ios-arrow-forward" color="#9ea7b4" />
              </div>
            </FormItem>
            <FormItem ref="occupationField" label="职业">
              <div class="mobile-trigger" @click="showOccupationPicker = true">
              <span :class="occupationDisplayText ? 'mobile-trigger__value' : 'mobile-trigger__placeholder'">
                {{ occupationDisplayText || '请选择职业' }}
              </span>
                <Icon type="ios-arrow-forward" color="#9ea7b4" />
              </div>
            </FormItem>
          </div>
          <FormItem ref="phoneField" label="联系电话" :class="numberFieldClasses('phone')">
            <Input
              :value="form.phone"
              readonly
              maxlength="11"
              placeholder="请输入手机号"
              @on-focus="openNumberKeyboard('phone', 'phoneField')"
            />
            <div v-if="numberFieldError('phone')" class="number-field-error-text">{{ numberFieldError('phone') }}</div>
          </FormItem>
          <FormItem label="证件类型">
            <div class="mobile-trigger" @click="showDocumentTypePicker = true">
              <span class="mobile-trigger__value">{{ documentTypeDisplayText }}</span>
              <Icon type="ios-arrow-forward" color="#9ea7b4" />
            </div>
          </FormItem>
          <FormItem ref="idNumberField" label="证件号码" :class="numberFieldClasses('idNumber')">
            <Input
              v-model="form.idNumber"
              :readonly="documentType === 0 && !idNumberSystemKeyboard"
              maxlength="18"
              :placeholder="documentType === 0 ? '请输入身份证号' : '请输入证件号码'"
              @on-focus="handleDocumentNumberFocus"
              @on-blur="handleIdNumberBlur"
            />
            <div v-if="numberFieldError('idNumber')" class="number-field-error-text">{{ numberFieldError('idNumber') }}</div>
          </FormItem>
          <FormItem ref="regionField" label="现居住地">
            <div class="mobile-trigger" @click="showRegionPicker = true">
            <span :class="regionDisplayText ? 'mobile-trigger__value' : 'mobile-trigger__placeholder'">
              {{ regionDisplayText || '请选择省市区' }}
            </span>
              <Icon type="ios-arrow-forward" color="#9ea7b4" />
            </div>
          </FormItem>
          <FormItem ref="addressField" label="详细地址">
            <Input v-model="form.address" maxlength="100" placeholder="请输入详细地址" />
          </FormItem>
          <FormItem v-if="showSignatureEntry" ref="signatureField" label="知情同意说明书">
            <Divider class="consent-divider-link" @click.native="openInsuranceModal">
              {{ signatureUrl ? '已完成知情同意说明书' : '知情同意说明书' }}
            </Divider>
          </FormItem>
          <div class="grid">
            <FormItem ref="heightField" label="身高" :class="numberFieldClasses('height')">
              <Input
                :value="form.height"
                readonly
                maxlength="5"
                placeholder="请输入身高"
                @on-focus="openNumberKeyboard('height', 'heightField')"
              >
                <span slot="append" class="number-field-unit">cm</span>
              </Input>
              <div v-if="numberFieldError('height')" class="number-field-error-text">{{ numberFieldError('height') }}</div>
            </FormItem>
            <FormItem ref="weightField" label="体重" :class="numberFieldClasses('weight')">
              <Input
                :value="form.weight"
                readonly
                maxlength="5"
                placeholder="请输入体重"
                @on-focus="openNumberKeyboard('weight', 'weightField')"
              >
                <span slot="append" class="number-field-unit">kg</span>
              </Input>
              <div v-if="numberFieldError('weight')" class="number-field-error-text">{{ numberFieldError('weight') }}</div>
            </FormItem>
          </div>
        </Form>
      </Card>

      <Card v-show="currentStep === 1" dis-hover class="panel">
        <p slot="title">健康信息采集</p>
        <Form label-position="top">
          <FormItem label="现有症状">
            <CheckboxGroup :value="survey.symptoms" @on-change="handleSymptomsChange">
              <Checkbox label="无症状" />
              <Checkbox label="腹痛" />
              <Checkbox label="反酸" />
              <Checkbox label="嗳气" />
              <Checkbox label="烧心" />
              <Checkbox label="胸骨后不适" />
            </CheckboxGroup>
          </FormItem>

          <FormItem ref="digestiveField" label="消化系统疾病史">
            <CheckboxGroup :value="survey.medicalHistory.digestiveSystem" @on-change="handleDigestiveChange">
              <Checkbox label="无消化系统疾病史" />
              <Checkbox label="胃食管反流病" />
              <Checkbox label="贲门失弛缓症" />
              <Checkbox label="胃癌" />
              <Checkbox label="结直肠癌" />
              <Checkbox label="其他消化道疾病" />
            </CheckboxGroup>
          </FormItem>

          <FormItem v-if="showOtherDigestive" ref="otherDigestiveField" label="其他消化道病史">
            <Input v-model="survey.medicalHistory.otherSymptomInfo" placeholder="请补充说明" />
          </FormItem>

          <FormItem ref="headNeckField" label="头颈部相关肿瘤史">
            <RadioGroup v-model="survey.medicalHistory.headAndNeck">
              <Radio label="有">有</Radio>
              <Radio label="无">无</Radio>
            </RadioGroup>
          </FormItem>

          <FormItem label="其他肿瘤病史">
            <RadioGroup v-model="otherTumourState">
              <Radio label="有">有</Radio>
              <Radio label="无">无</Radio>
            </RadioGroup>
          </FormItem>

          <FormItem v-if="otherTumourState === '有'" label="其他肿瘤病史说明">
            <Input v-model="survey.medicalHistory.otherCancerHistory" placeholder="请补充说明" />
          </FormItem>

          <FormItem ref="oralHealthField" label="是否缺齿">
            <RadioGroup v-model="survey.oralHealth.noMissingTeeth">
              <Radio label="是">是</Radio>
              <Radio label="否">否</Radio>
            </RadioGroup>
          </FormItem>

          <FormItem
            v-if="survey.oralHealth.noMissingTeeth === '是'"
            ref="missingTeethField"
            label="缺齿数"
            :class="numberFieldClasses('missingTeeth')"
          >
            <Input
              :value="survey.oralHealth.missingTeeth || ''"
              readonly
              maxlength="2"
              placeholder="请输入缺齿数"
              @on-focus="openNumberKeyboard('missingTeeth', 'missingTeethField')"
            />
          </FormItem>

          <FormItem ref="lifestyleField" label="不良生活习惯">
            <CheckboxGroup :value="survey.unhealthyLifestyles" @on-change="handleLifestyleChange">
              <Checkbox label="无" />
              <Checkbox label="吸烟" />
              <Checkbox label="饮酒" />
              <Checkbox label="热烫饮食" />
              <Checkbox label="腌制饮食" />
              <Checkbox label="吃饭过快" />
            </CheckboxGroup>
          </FormItem>

          <FormItem ref="familyFirstField" label="一级亲属患食管癌">
            <RadioGroup v-model="survey.familyHistory.first.has">
              <Radio label="是">是</Radio>
              <Radio label="否">否</Radio>
            </RadioGroup>
          </FormItem>

          <div v-if="survey.familyHistory.first.has === '是'" ref="familyFirstDetailField" class="grid">
            <FormItem label="具体关系">
              <button type="button" class="relation-field" @click="openRelationPicker('first')"><span>{{ survey.familyHistory.first.details || '请选择具体关系（多选）' }}</span><Icon type="ios-arrow-down" /></button>
            </FormItem>
            <FormItem ref="familyFirstNumberField" label="人数" :class="numberFieldClasses('familyFirstNumber')">
              <Input
                :value="survey.familyHistory.first.number || ''"
                readonly
                maxlength="2"
                placeholder="请填写人数"
                @on-focus="openNumberKeyboard('familyFirstNumber', 'familyFirstNumberField')"
              />
            </FormItem>
          </div>

          <FormItem ref="familySecondField" label="二级亲属患食管癌">
            <RadioGroup v-model="survey.familyHistory.second.has">
              <Radio label="是">是</Radio>
              <Radio label="否">否</Radio>
            </RadioGroup>
          </FormItem>

          <div v-if="survey.familyHistory.second.has === '是'" ref="familySecondDetailField" class="grid">
            <FormItem label="具体关系">
              <button type="button" class="relation-field" @click="openRelationPicker('second')"><span>{{ survey.familyHistory.second.details || '请选择具体关系（多选）' }}</span><Icon type="ios-arrow-down" /></button>
            </FormItem>
            <FormItem ref="familySecondNumberField" label="人数" :class="numberFieldClasses('familySecondNumber')">
              <Input
                :value="survey.familyHistory.second.number || ''"
                readonly
                maxlength="2"
                placeholder="请填写人数"
                @on-focus="openNumberKeyboard('familySecondNumber', 'familySecondNumberField')"
              />
            </FormItem>
          </div>

          <FormItem label="其他肿瘤家族史">
            <Input v-model="survey.familyHistory.relativesOtherCancer" placeholder="请填写其他肿瘤家族史" />
          </FormItem>

          <FormItem ref="consentField" class="consent-item">
            <Checkbox v-model="survey.consentAgreed">
              阅读并同意
              <span class="consent-link" @click.stop="showConsentModal = true">《早期食管癌筛查精准风险评测检查知情同意书》</span>
            </Checkbox>
          </FormItem>
        </Form>
      </Card>

      <div class="action-bar">
        <template v-if="currentStep === 0">
          <Button long type="primary" size="large" @click="goNextStep">下一步：填写健康信息</Button>
        </template>
        <template v-else>
          <div class="action-bar__row">
            <Button size="large" @click="currentStep = 0">上一步</Button>
            <Button type="primary" size="large" :loading="saving" @click="submitAll">提交信息</Button>
          </div>
        </template>
      </div>
    </template>

    <van-popup v-model="showRegionPicker" position="bottom" get-container="body" round>
      <van-area
        title="选择省市区"
        :area-list="areaList"
        :value="currentAreaCode"
        @cancel="showRegionPicker = false"
        @confirm="handleRegionConfirm"
      />
    </van-popup>

    <van-popup v-model="showSexPicker" position="bottom" get-container="body" round>
      <van-picker
        show-toolbar
        title="选择性别"
        :columns="sexOptions"
        :default-index="sexDefaultIndex"
        @cancel="showSexPicker = false"
        @confirm="handleSexConfirm"
      />
    </van-popup>

    <van-popup v-model="showDocumentTypePicker" position="bottom" get-container="body" round>
      <van-picker
        show-toolbar
        title="选择证件类型"
        :columns="documentTypeOptions"
        :default-index="documentType"
        @cancel="showDocumentTypePicker = false"
        @confirm="handleDocumentTypeConfirm"
      />
    </van-popup>

    <van-popup v-model="showBirthPicker" position="bottom" get-container="body" round>
      <van-datetime-picker
        type="date"
        title="选择出生日期"
        :value="birthPickerDate"
        :min-date="minBirthDate"
        :max-date="maxBirthDate"
        @cancel="showBirthPicker = false"
        @confirm="handleBirthConfirm"
      />
    </van-popup>

    <van-popup v-model="showRelationPicker" position="bottom" get-container="body" round>
      <section class="relation-sheet" role="dialog" aria-modal="true" aria-label="选择具体关系">
        <header class="relation-sheet__header"><div><b>{{ relationType === 'first' ? '一级亲属' : '二级亲属' }} · 具体关系</b><p>可选择多个关系，点击确定保存</p></div><button type="button" aria-label="关闭" @click="showRelationPicker = false"><Icon type="ios-close" /></button></header>
        <div class="relation-sheet__options">
          <label v-for="option in relationOptions" :key="option" :class="['relation-option', { selected: relationDraft.indexOf(option) !== -1 }]"><input v-model="relationDraft" type="checkbox" :value="option"><span>{{ option }}</span></label>
        </div>
        <footer class="relation-sheet__footer"><span>已选 {{ relationDraft.length }} 项</span><button type="button" @click="confirmRelationPicker">确定</button></footer>
      </section>
    </van-popup>

    <van-popup v-model="showOccupationPicker" position="bottom" get-container="body" round>
      <van-picker
        show-toolbar
        title="选择职业"
        :columns="occupationOptions"
        :default-index="occupationDefaultIndex"
        @cancel="showOccupationPicker = false"
        @confirm="handleOccupationConfirm"
      />
    </van-popup>

    <van-number-keyboard
      :show="numberKeyboardVisible"
      :title="numberKeyboardTitle"
      :value="numberKeyboardValue"
      :maxlength="numberKeyboardMaxLength"
      :extra-key="numberKeyboardExtraKey"
      :z-index="3100"
      close-button-text="完成"
      delete-button-text="删除"
      @input="handleNumberKeyboardInput"
      @delete="handleNumberKeyboardDelete"
      @show="handleNumberKeyboardShown"
      @close="closeNumberKeyboard"
      @blur="closeNumberKeyboard"
    >
      <div slot="title-left" class="number-keyboard-actions" @touchstart.stop>
        <button
          v-if="numberKeyboardValue"
          type="button"
          class="number-keyboard-action"
          @click.stop="clearNumberKeyboardValue"
        >清空</button>
        <button
          v-if="activeNumberField === 'idNumber'"
          type="button"
          class="number-keyboard-action"
          @click.stop="useSystemIdNumberKeyboard"
        >系统键盘</button>
      </div>
    </van-number-keyboard>

    <Modal v-model="showConsentModal" title="知情同意书">
      <div class="consent-modal">
        <h3>早期食管癌筛查精准风险评测检查知情同意书</h3>
        <h4>一、注意事项：</h4>
        <ol>
          <li>本次筛查适用16周岁以上人群，高龄人员应在亲属陪同下完成取样；</li>
          <li>本项筛查是对口咽部、食管、贲门三个部位取样，空腹或餐后2-3小时进行筛查；</li>
          <li>本项筛查出具综合风险评测报告，由医院联合怡早医疗科技（山东）有限公司协调实验室检测并出具报告。</li>
        </ol>
        <h4>二、早期食管癌筛查筛查禁忌症：</h4>
        <ol>
          <li>近期有上消化道及咽喉部手术病史、有活动性上消化道出血病史者；</li>
          <li>患有严重上消化道系统疾病者，如上消化道腐蚀性炎症急性期或疑为上消化道穿孔；</li>
          <li>患有严重心脏疾病和严重神经系统疾病者，不能耐受及配合检查者；</li>
          <li>患有严重的呼吸系统疾病者，包括各种原因所致的呼吸困难，如哮喘持续状态及其他原因所致的肺功能差不能耐受检查者；</li>
          <li>严重的肝肾功能异常者，包括门静脉高压所致的食管胃底静脉曲张者；</li>
          <li>有出血倾向者（凝血功能异常，血小板严重降低）或正在使用抗凝血药物者，后者须停药一周后检查出凝血功能正常方可进行相关检查；</li>
          <li>咽后壁脓肿，严重脊柱畸形或主动脉瘤患者；</li>
          <li>各种原因所致的吞咽困难者，包括严重的咽喉部疾病病史的患者；</li>
          <li>各种原因所致的食管狭窄（畸形）；各种原因所致的食管瘘的患者，如食管癌术后或者放化疗术后存在瘘的患者；</li>
          <li>不明原因的严重的胸痛及上腹痛者；</li>
          <li>妊娠期妇女和儿童。</li>
        </ol>
        <h4>三、本项筛查可能会发生的不良反应：</h4>
        <ol>
          <li>使用一次性病变细胞采集器时有一根提线留在咽喉处，会产生轻微异物感、恶心感，喝水吞咽可以缓解；</li>
          <li>取样后可能会出现咳嗽，少数人可能出现短暂的恶心、胸闷，这些反应通常在几分钟后消失，如有持续不适应立即告知工作人员；</li>
          <li>取样后有极少数人可能出现咳嗽，持续不退或者痰中带血，如有以上情况应立即告知工作人员或去医院就诊；</li>
          <li>按照工作人员指导规范进行筛查，尽量避免不良反应发生。</li>
        </ol>
        <p>本人对以上提示已详知，理解了全部情况，熟悉取样流程，无第二项提到的禁忌症状，同意参加此次筛查。</p>
      </div>
      <div slot="footer" class="consent-footer">
        <Button @click="showConsentModal = false">关闭</Button>
        <Button type="primary" @click="confirmConsent">确认</Button>
      </div>
    </Modal>

    <div v-if="showInsuranceModal" class="signature-page">
      <div class="signature-page__header">
        <button type="button" class="signature-page__back" @click="closeSignaturePage">
          <Icon type="ios-arrow-back" />
          <span>返回</span>
        </button>
        <div class="signature-page__title">知情同意说明书</div>
        <div class="signature-page__spacer"></div>
      </div>
      <div :class="['signature-page__body', { 'signature-page__body--drawing': signatureDrawing }]">
        <div class="signature-page__content">
          <div
            v-if="signaturePageMode === 'insurance' && signatureHtmlPreview"
            class="insurance-html-preview"
            v-html="signatureHtmlPreview"
          ></div>
          <img
            v-else-if="signatureDisplayImageUrl"
            class="signature-contract-image"
            :src="signatureDisplayImageUrl"
            alt="知情同意说明书"
          />
          <div v-else class="insurance-html-preview" v-html="defaultConsentHtml"></div>
        </div>
        <div v-if="signatureUrl" class="signature-page__preview">
          <div class="signature-page__preview-title">已签署内容</div>
          <img :src="signatureUrl" alt="signature" />
        </div>
        <div class="signature-card insurance-signature-card">
          <canvas
            v-show="!signatureUrl"
            ref="signatureCanvas"
            class="signature-canvas"
            @mousedown="startSignature"
            @mousemove="moveSignature"
            @mouseup="endSignature"
            @mouseleave="endSignature"
            @touchstart.prevent="startSignature"
            @touchmove.prevent="moveSignature"
            @touchend.prevent="endSignature"
          ></canvas>
          <div class="signature-tip">
            {{ signatureUrl ? '已完成签名，如需重签请先清除再重新书写。' : '请在签名区域内手写签名。' }}
          </div>
        </div>
      </div>
      <div class="signature-page__footer">
        <template v-if="signatureUrl">
          <Button size="large" @click="closeSignaturePage">返回</Button>
          <Button size="large" type="primary" @click="clearSignature">重签</Button>
        </template>
        <template v-else>
          <Button size="large" @click="clearSignature">清除</Button>
          <Button size="large" type="primary" :loading="signatureUploading" @click="uploadSignature">保存签名</Button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import Qs from 'qs'
import { areaList } from '@vant/area-data'
import { regionData } from 'element-china-area-data'
import { setToken, getToken, getAuthMeta, setAuthMeta, clearAuthSession } from '../utils/auth'
import { isWechatBrowser as detectWechatBrowser, detectWechatRuntime, configWechatJssdk } from '../utils/wechat'
import { isEmbeddedPage, needsParentWechat, scanByParent } from '../utils/parentWechat'

const SUBMIT_DEDUP_KEY = 'wpc_h5_last_submit_signature'
const LEGACY_CZ_SIGNATURE_IMAGE = 'https://app.n-ecdc.net/wpc-os/contract/zhiqingshu.png'
const DEFAULT_INSURANCE_IMAGE_URL = 'https://app.n-ecdc.net/wpc-os/contract/baoxian.png'
const DEFAULT_SIGNATURE_COMPOSE_CONFIG = {
  signXRatio: 0.21,
  signYRatio: 0.88,
  signWidthRatio: 0.0767,
  signHeightRatio: 0.04,
  dateXRatio: 0.21,
  dateYRatio: 0.946,
  dateFontSize: 24
}

const DEFAULT_CONSENT_HTML = `
  <div class="consent-modal">
    <h3>早期食管癌筛查精准风险评测检查知情同意书</h3>
    <h4>一、注意事项：</h4>
    <ol>
      <li>本次筛查适用16周岁以上人群，高龄人员应在亲属陪同下完成取样；</li>
      <li>本项筛查是对口咽部、食管、贲门三个部位取样，空腹或餐后2-3小时进行筛查；</li>
      <li>本项筛查出具综合风险评测报告，由医院联合怡早医疗科技（山东）有限公司协调实验室检测并出具报告。</li>
    </ol>
    <h4>二、早期食管癌筛查禁忌症：</h4>
    <ol>
      <li>近期有上消化道及咽喉部手术病史、有活动性上消化道出血病史者；</li>
      <li>患有严重上消化道系统疾病者，如上消化道腐蚀性炎症急性期或疑为上消化道穿孔；</li>
      <li>患有严重心脏疾病和严重神经系统疾病者，不能耐受及配合检查者；</li>
      <li>患有严重的呼吸系统疾病者，包括各种原因所致的呼吸困难及肺功能差不能耐受检查者；</li>
      <li>患有严重的肝肾功能异常者，包括门静脉高压所致的食管胃底静脉曲张者；</li>
      <li>有出血倾向者或正在使用抗凝血药物者，须停药并经评估后方可检查；</li>
      <li>咽后壁脓肿，严重脊柱畸形或主动脉瘤患者；</li>
      <li>各种原因所致的吞咽困难者；</li>
      <li>各种原因所致的食管狭窄、食管瘘患者；</li>
      <li>不明原因的严重胸痛及上腹痛者；</li>
      <li>妊娠期妇女和儿童。</li>
    </ol>
  </div>
`

function createSurvey () {
  return {
    version: '1.0.0',
    consentAgreed: false,
    symptoms: [],
    medicalHistory: {
      digestiveSystem: [],
      otherSymptomInfo: '',
      headAndNeck: '无',
      otherCancerHistory: ''
    },
    familyHistory: {
      first: { has: '否', details: '', number: 0 },
      second: { has: '否', details: '', number: 0 },
      relativesOtherCancer: ''
    },
    unhealthyLifestyles: ['无'],
    oralHealth: {
      noMissingTeeth: '否',
      missingTeeth: 0
    }
  }
}

function resolveExclusiveSelection (nextValues, prevValues, exclusiveLabel) {
  const next = Array.isArray(nextValues) ? nextValues.slice() : []
  const prev = Array.isArray(prevValues) ? prevValues : []
  const hadExclusive = prev.indexOf(exclusiveLabel) > -1
  const hasExclusive = next.indexOf(exclusiveLabel) > -1

  if (hasExclusive && !hadExclusive) {
    return [exclusiveLabel]
  }
  if (hasExclusive && next.length > 1) {
    return next.filter(item => item !== exclusiveLabel)
  }
  if (!hasExclusive && hadExclusive) {
    return next.filter(Boolean)
  }
  return next
}

function isPositiveNumber (value) {
  const normalized = String(value === undefined || value === null ? '' : value).trim()
  if (!normalized) {
    return false
  }
  return Number(normalized) > 0
}

function isValidIdNumber (value) {
  const idNumber = String(value === undefined || value === null ? '' : value).trim().toUpperCase()
  if (!/^\d{17}[\dX]$/.test(idNumber)) {
    return false
  }
  const factors = [7, 9, 10, 5, 8, 4, 2, 1, 6, 3, 7, 9, 10, 5, 8, 4, 2]
  const parity = ['1', '0', 'X', '9', '8', '7', '6', '5', '4', '3', '2']
  let sum = 0
  for (let i = 0; i < 17; i++) {
    sum += Number(idNumber.charAt(i)) * factors[i]
  }
  return parity[sum % 11] === idNumber.charAt(17)
}

function getSexFromIdNumber (value) {
  const idNumber = String(value === undefined || value === null ? '' : value).trim().toUpperCase()
  if (!isValidIdNumber(idNumber)) {
    return ''
  }
  return Number(idNumber.charAt(16)) % 2 === 0 ? 'false' : 'true'
}

function getAgeFromIdNumber (value) {
  const idNumber = String(value === undefined || value === null ? '' : value).trim().toUpperCase()
  if (!isValidIdNumber(idNumber)) {
    return ''
  }
  const year = Number(idNumber.substring(6, 10))
  const month = Number(idNumber.substring(10, 12))
  const day = Number(idNumber.substring(12, 14))
  const now = new Date()
  let age = now.getFullYear() - year
  const currentMonth = now.getMonth() + 1
  const currentDay = now.getDate()
  if (currentMonth < month || (currentMonth === month && currentDay < day)) {
    age -= 1
  }
  return age > 0 ? String(age) : ''
}

function getBirthDateFromIdNumber (value) {
  const idNumber = String(value === undefined || value === null ? '' : value).trim().toUpperCase()
  if (!isValidIdNumber(idNumber)) {
    return ''
  }
  return [
    idNumber.substring(6, 10),
    idNumber.substring(10, 12),
    idNumber.substring(12, 14)
  ].join('-')
}

function formatDateValue (value) {
  const date = value instanceof Date ? value : new Date(value)
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return ''
  }
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return year + '-' + month + '-' + day
}

function normalizeDigits (value, maxLength) {
  const digits = String(value === undefined || value === null ? '' : value).replace(/\D/g, '')
  return maxLength ? digits.slice(0, maxLength) : digits
}

function normalizeDecimalInput (value, maxIntegerLength) {
  const text = String(value === undefined || value === null ? '' : value).replace(/[^\d.]/g, '')
  const parts = text.split('.')
  const integerPart = (parts[0] || '').slice(0, maxIntegerLength || (parts[0] || '').length)
  if (parts.length <= 1) {
    return integerPart
  }
  const decimalPart = parts.slice(1).join('').replace(/\./g, '')
  return integerPart + '.' + decimalPart.slice(0, 1)
}

function normalizeCoordinate (value, min, max) {
  const text = String(value === undefined || value === null ? '' : value).trim()
  if (!text) return ''
  const coordinate = Number(text)
  if (!isFinite(coordinate) || coordinate < min || coordinate > max) return ''
  return String(coordinate)
}

function parseQueryString (queryString) {
  const result = {}
  const normalized = String(queryString || '').replace(/^[?#&]/, '')
  if (!normalized) {
    return result
  }
  normalized.split('&').forEach(pair => {
    if (!pair) {
      return
    }
    const segments = pair.split('=')
    const key = decodeURIComponent(segments[0] || '').trim()
    if (!key) {
      return
    }
    const value = decodeURIComponent(segments.slice(1).join('=') || '')
    result[key] = value
  })
  return result
}

function mergeQueryObjects () {
  const merged = {}
  for (let i = 0; i < arguments.length; i++) {
    const current = arguments[i] || {}
    Object.keys(current).forEach(key => {
      if (current[key] !== undefined && current[key] !== null && current[key] !== '') {
        merged[key] = current[key]
      }
    })
  }
  return merged
}

function normalizeScannedValue (value) {
  const text = String(value || '').trim()
  if (!text) {
    return ''
  }
  if (text.indexOf(',') > -1 && text.indexOf('http') !== 0) {
    const parts = text.split(',')
    return String(parts[parts.length - 1] || '').trim()
  }
  return text
}

function parseCheckEntryCodeFromUrl (value) {
  const text = String(value || '').trim()
  if (!text) {
    return { code: '', message: '请先扫描或输入正确的检查单二维码内容' }
  }
  let url
  try {
    url = new URL(text)
  } catch (e) {
    return { code: '', message: '请扫描正确的检查单二维码' }
  }
  const protocol = String(url.protocol || '').toLowerCase()
  const host = String(url.host || '').toLowerCase()
  const pathname = String(url.pathname || '').replace(/\/+$/, '') || '/'
  if (protocol !== 'https:' || host !== 'app.n-ecdc.com' || pathname !== '/entry') {
    return { code: '', message: '请扫描正确的检查单二维码' }
  }
  const code = String(url.searchParams.get('code') || '').trim()
  if (!code) {
    return { code: '', message: '请扫描正确的检查单二维码' }
  }
  return { code: code, message: '' }
}

export default {
  name: 'IntakeSurvey',
  data () {
    return {
      loading: false,
      entryCheckId: '',
      showRelationPicker: false,
      relationType: 'first',
      relationDraft: [],
      relationOptions: [],
      authBlocked: false,
      authErrorMessage: '',
      saving: false,
      bootstrapping: false,
      pendingBootstrapTimer: null,
      currentStep: 0,
      wxReady: false,
      wxInitError: '',
      runtimeEnv: 'unknown',
      latitude: '',
      longitude: '',
      accuracy: '',
      locationRequested: false,
      browserLocationRequested: false,
      siteId: '',
      insuranceHtml: '',
      insuranceEnabled: true,
      siteLocked: false,
      siteResolving: false,
      signatureUrl: '',
      signatureUploading: false,
      signatureDirty: false,
      signatureDrawing: false,
      signaturePageMode: '',
      signatureHtmlPreview: '',
      defaultConsentHtml: DEFAULT_CONSENT_HTML,
      signatureDisplayImageUrl: '',
      signatureComposeBaseImageUrl: '',
      signatureComposeEnabled: false,
      signatureComposeConfig: Object.assign({}, DEFAULT_SIGNATURE_COMPOSE_CONFIG),
      authMode: '',
      externalUserId: '',
      envKey: '',
      loginTicket: '',
      scanResultText: '',
      patientId: '',
      createNew: false,
      returnTo: '',
      entrySection: 'basic',
      code: '',
      showSexPicker: false,
      showDocumentTypePicker: false,
      showBirthPicker: false,
      showOccupationPicker: false,
      showRegionPicker: false,
      showConsentModal: false,
      showInsuranceModal: false,
      numberKeyboardVisible: false,
      activeNumberField: '',
      activeNumberFieldRef: '',
      idNumberSystemKeyboard: false,
      idNumberKeyboardSwitching: false,
      numberFieldTouched: {
        age: false,
        phone: false,
        idNumber: false,
        height: false,
        weight: false
      },
      areaList: areaList,
      regionOptions: regionData,
      sexOptions: ['男', '女'],
      documentTypeOptions: [
        '二代身份证',
        '港澳居民来往内地通行证',
        '台湾居民来往大陆通行证',
        '港澳台居民身份证',
        '外国永久居留身份证号'
      ],
      occupationOptions: [],
      documentType: 0,
      form: {
        trueName: '',
        sex: 'true',
        age: '',
        birth: '',
        occupation: '',
        phone: '',
        idNumber: '',
        regionCodes: [],
        regionNames: [],
        address: '',
        height: '',
        weight: ''
      },
      survey: createSurvey(),
      otherTumourState: '无'
    }
  },
  computed: {
    showOtherDigestive () {
      return this.survey.medicalHistory.digestiveSystem.indexOf('其他消化道疾病') > -1
    },
    sexDisplayText () {
      return this.form.sex === 'true' ? '男' : (this.form.sex === 'false' ? '女' : '')
    },
    sexDefaultIndex () {
      return this.form.sex === 'false' ? 1 : 0
    },
    documentTypeDisplayText () {
      return this.documentTypeOptions[this.documentType] || this.documentTypeOptions[0]
    },
    occupationDisplayText () {
      return this.form.occupation || ''
    },
    occupationDefaultIndex () {
      const index = this.occupationOptions.indexOf(this.form.occupation)
      return index > -1 ? index : 0
    },
    birthPickerDate () {
      const parsed = this.form.birth ? new Date(this.form.birth + 'T00:00:00') : null
      return parsed && !isNaN(parsed.getTime()) ? parsed : this.maxBirthDate
    },
    minBirthDate () {
      return new Date(1900, 0, 1)
    },
    maxBirthDate () {
      return new Date()
    },
    regionDisplayText () {
      return (this.form.regionNames || []).filter(Boolean).join(' ')
    },
    currentAreaCode () {
      const regionCodes = this.form.regionCodes || []
      return regionCodes[2] || regionCodes[1] || regionCodes[0] || ''
    },
    hasLockedCheckId () {
      return !!(this.patientId && String(this.code || '').trim())
    },
    shouldSkipInsuranceFlow () {
      return !!(this.code && String(this.code).indexOf('CZ') === 0)
    },
    showSignatureEntry () {
      return true
    },
    isWechatBrowser () {
      return detectWechatBrowser()
    },
    isEmbeddedFrame () {
      return isEmbeddedPage()
    },
    returnLabel () {
      return String(this.returnTo || '').indexOf('/projects/') === 0 ? '返回检测详情' : '返回首页'
    },
    numberKeyboardValue () {
      return this.activeNumberField ? String(this.getNumberFieldValue(this.activeNumberField) || '') : ''
    },
    numberKeyboardExtraKey () {
      if (this.activeNumberField === 'height' || this.activeNumberField === 'weight') return '.'
      if (this.activeNumberField === 'idNumber') return 'X'
      return ''
    },
    numberKeyboardMaxLength () {
      const lengths = {
        age: 3,
        phone: 11,
        idNumber: 18,
        height: 5,
        weight: 5,
        missingTeeth: 2,
        familyFirstNumber: 2,
        familySecondNumber: 2
      }
      return lengths[this.activeNumberField] || 18
    },
    numberKeyboardTitle () {
      if (this.activeNumberField === 'idNumber') return ''
      const titles = {
        age: '请输入年龄',
        phone: '请输入联系电话',
        idNumber: '请输入身份证号',
        height: '请输入身高(cm)',
        weight: '请输入体重(kg)',
        missingTeeth: '请输入缺齿数',
        familyFirstNumber: '请输入一级亲属人数',
        familySecondNumber: '请输入二级亲属人数'
      }
      return titles[this.activeNumberField] || '请输入数字'
    }
  },
  watch: {
    otherTumourState (value) {
      if (value === '无') {
        this.survey.medicalHistory.otherCancerHistory = ''
      }
    },
    'form.phone' (value) {
      const normalized = normalizeDigits(value, 11)
      if (normalized !== value) {
        this.form.phone = normalized
      }
    },
    'form.idNumber' (value) {
      const normalized = String(value === undefined || value === null ? '' : value).trim().toUpperCase()
      if (normalized !== value) {
        this.form.idNumber = normalized
        return
      }
      if (this.documentType !== 0) return
      const sex = getSexFromIdNumber(normalized)
      if (sex) {
        this.form.sex = sex
      }
      const birth = getBirthDateFromIdNumber(normalized)
      if (birth) {
        this.form.birth = birth
      }
    },
    'form.age' (value) {
      const normalized = normalizeDigits(value, 3)
      if (normalized !== value) {
        this.form.age = normalized
      }
    },
    'form.height' (value) {
      const normalized = normalizeDecimalInput(value, 3)
      if (normalized !== value) {
        this.form.height = normalized
      }
    },
    'form.weight' (value) {
      const normalized = normalizeDecimalInput(value, 3)
      if (normalized !== value) {
        this.form.weight = normalized
      }
    },
    'survey.oralHealth.noMissingTeeth' (value) {
      if (value === '否') {
        this.survey.oralHealth.missingTeeth = 0
      }
    },
    'survey.familyHistory.first.has' (value) {
      if (value === '否') {
        this.survey.familyHistory.first.details = ''
        this.survey.familyHistory.first.number = 0
      }
    },
    'survey.familyHistory.second.has' (value) {
      if (value === '否') {
        this.survey.familyHistory.second.details = ''
        this.survey.familyHistory.second.number = 0
      }
    },
    'survey.medicalHistory.digestiveSystem' (value) {
      if (value.indexOf('其他消化道疾病') === -1) {
        this.survey.medicalHistory.otherSymptomInfo = ''
      }
    },
    signatureUrl (value) {
      this.survey.consentAgreed = !!value
    },
    '$route.fullPath' () {
      this.scheduleBootstrap('route-change')
    }
  },
  async created () {
    this.bindExternalEntryListeners()
    await this.bootstrap()
  },
  beforeDestroy () {
    this.unbindExternalEntryListeners()
    if (this.pendingBootstrapTimer) {
      window.clearTimeout(this.pendingBootstrapTimer)
      this.pendingBootstrapTimer = null
    }
  },
  methods: {
    openRelationPicker (type) {
      this.closeNumberKeyboard()
      this.relationType = type
      const stored = String(this.survey.familyHistory[type].details || '').split(/[、,，;；]/).map(value => value.trim()).filter(Boolean)
      const options = type === 'first'
        ? ['父亲', '母亲', '兄弟', '姐妹', '儿子', '女儿']
        : ['爷爷', '奶奶', '外公', '外婆', '伯伯', '叔叔', '姑姑', '舅舅', '姨妈']
      // Keep previously entered free text selectable when reopening older records.
      this.relationOptions = Array.from(new Set(options.concat(stored)))
      this.relationDraft = Array.from(new Set(stored))
      this.showRelationPicker = true
    },
    confirmRelationPicker () {
      const selectedRelations = this.relationOptions.filter(option => this.relationDraft.indexOf(option) !== -1)
      const family = this.survey.familyHistory[this.relationType]
      family.details = selectedRelations.join('、')
      family.number = selectedRelations.length ? selectedRelations.length : ''
      this.showRelationPicker = false
    },
    bindExternalEntryListeners () {
      window.addEventListener('hashchange', this.handleExternalEntryChanged, false)
      window.addEventListener('pageshow', this.handleExternalEntryChanged, false)
      document.addEventListener('visibilitychange', this.handleVisibilityRefresh, false)
    },
    unbindExternalEntryListeners () {
      window.removeEventListener('hashchange', this.handleExternalEntryChanged, false)
      window.removeEventListener('pageshow', this.handleExternalEntryChanged, false)
      document.removeEventListener('visibilitychange', this.handleVisibilityRefresh, false)
    },
    handleExternalEntryChanged () {
      this.scheduleBootstrap('external-entry-changed')
    },
    handleVisibilityRefresh () {
      if (!document.hidden) {
        this.scheduleBootstrap('visibilitychange')
      }
    },
    scheduleBootstrap (reason) {
      if (this.pendingBootstrapTimer) {
        window.clearTimeout(this.pendingBootstrapTimer)
      }
      this.pendingBootstrapTimer = window.setTimeout(() => {
        this.pendingBootstrapTimer = null
        this.bootstrap(reason)
      }, 120)
    },
    getNumberFieldValue (field) {
      if (field === 'missingTeeth') return this.survey.oralHealth.missingTeeth
      if (field === 'familyFirstNumber') return this.survey.familyHistory.first.number
      if (field === 'familySecondNumber') return this.survey.familyHistory.second.number
      return this.form[field]
    },
    setNumberFieldValue (field, value) {
      if (field === 'missingTeeth') {
        this.survey.oralHealth.missingTeeth = value
      } else if (field === 'familyFirstNumber') {
        this.survey.familyHistory.first.number = value
      } else if (field === 'familySecondNumber') {
        this.survey.familyHistory.second.number = value
      } else {
        this.form[field] = value
      }
    },
    openNumberKeyboard (field, fieldRef) {
      if (field === 'idNumber' && this.idNumberSystemKeyboard) return
      this.activeNumberField = field
      this.activeNumberFieldRef = fieldRef
      this.numberKeyboardVisible = true
      this.$nextTick(() => {
        window.setTimeout(() => this.scrollNumberFieldIntoView(), 180)
      })
    },
    handleDocumentNumberFocus () {
      if (this.documentType === 0) {
        this.openNumberKeyboard('idNumber', 'idNumberField')
      }
    },
    closeNumberKeyboard () {
      const field = this.activeNumberField
      if (field) this.$set(this.numberFieldTouched, field, true)
      if ((field === 'height' || field === 'weight') && /\.$/.test(String(this.form[field] || ''))) {
        this.form[field] = String(this.form[field]).slice(0, -1)
      }
      this.numberKeyboardVisible = false
      const fieldComponent = this.$refs[this.activeNumberFieldRef]
      const fieldElement = fieldComponent && fieldComponent.$el ? fieldComponent.$el : fieldComponent
      const inputElement = fieldElement && fieldElement.querySelector ? fieldElement.querySelector('input') : null
      if (inputElement && typeof inputElement.blur === 'function') inputElement.blur()
    },
    useSystemIdNumberKeyboard () {
      this.numberKeyboardVisible = false
      this.idNumberSystemKeyboard = true
      this.idNumberKeyboardSwitching = true
      this.$nextTick(() => {
        const fieldComponent = this.$refs.idNumberField
        const fieldElement = fieldComponent && fieldComponent.$el ? fieldComponent.$el : fieldComponent
        const inputElement = fieldElement && fieldElement.querySelector ? fieldElement.querySelector('input') : null
        if (!inputElement || typeof inputElement.focus !== 'function') {
          this.idNumberKeyboardSwitching = false
          return
        }
        if (typeof inputElement.blur === 'function') inputElement.blur()
        window.setTimeout(() => {
          inputElement.focus()
          this.idNumberKeyboardSwitching = false
        }, 50)
      })
    },
    handleIdNumberBlur () {
      if (this.documentType !== 0) {
        this.$set(this.numberFieldTouched, 'idNumber', true)
        return
      }
      if (!this.idNumberSystemKeyboard) return
      if (this.idNumberKeyboardSwitching) return
      this.$set(this.numberFieldTouched, 'idNumber', true)
      window.setTimeout(() => {
        this.idNumberSystemKeyboard = false
      }, 100)
    },
    handleNumberKeyboardInput (key) {
      const field = this.activeNumberField
      if (!field) return
      let value = String(this.getNumberFieldValue(field) || '')
      const maxLength = this.numberKeyboardMaxLength
      if (value.length >= maxLength) return

      if (key === '.') {
        if (field !== 'height' && field !== 'weight') return
        if (value.indexOf('.') > -1) return
        value = value ? value + '.' : '0.'
      } else if (key === 'X') {
        if (field !== 'idNumber' || value.length !== 17 || value.indexOf('X') > -1) return
        value += 'X'
      } else if (/^\d$/.test(String(key))) {
        if (field === 'idNumber' && value.indexOf('X') > -1) return
        value += String(key)
      }
      this.setNumberFieldValue(field, value)
    },
    handleNumberKeyboardDelete () {
      const field = this.activeNumberField
      if (!field) return
      const value = String(this.getNumberFieldValue(field) || '')
      this.setNumberFieldValue(field, value.slice(0, -1))
    },
    clearNumberKeyboardValue () {
      const field = this.activeNumberField
      if (!field) return
      this.setNumberFieldValue(field, '')
      this.$set(this.numberFieldTouched, field, true)
    },
    numberFieldClasses (field) {
      return {
        'number-field-active': this.numberKeyboardVisible && this.activeNumberField === field,
        'number-field-error': !!this.numberFieldError(field)
      }
    },
    numberFieldError (field) {
      if (!this.numberFieldTouched[field]) return ''
      const value = String(this.form[field] || '').trim()
      if (!value) {
        const emptyMessages = {
          age: '请输入年龄',
          phone: '请输入联系电话',
          idNumber: this.documentType === 0 ? '请输入身份证号' : '请输入证件号码',
          height: '请输入身高',
          weight: '请输入体重'
        }
        return emptyMessages[field] || ''
      }
      if (field === 'age') {
        const age = Number(value)
        return /^\d+$/.test(value) && age >= 16 && age <= 120 ? '' : '年龄应在16至120岁之间'
      }
      if (field === 'phone') {
        return /^1\d{10}$/.test(value) ? '' : '请输入正确的手机号'
      }
      if (field === 'idNumber') {
        if (this.documentType === 0) {
          return isValidIdNumber(value) ? '' : '请输入正确的身份证号'
        }
        return /^[0-9A-Z]+$/.test(value) ? '' : '请输入正确的证件号码'
      }
      if (field === 'height') {
        const height = Number(value)
        return isPositiveNumber(value) && height >= 80 && height <= 250 ? '' : '身高应在80至250厘米之间'
      }
      if (field === 'weight') {
        const weight = Number(value)
        return isPositiveNumber(value) && weight >= 20 && weight <= 300 ? '' : '体重应在20至300公斤之间'
      }
      return ''
    },
    markNumberFieldTouchedByRef (fieldRef) {
      const fieldMap = {
        ageField: 'age',
        phoneField: 'phone',
        idNumberField: 'idNumber',
        heightField: 'height',
        weightField: 'weight'
      }
      const field = fieldMap[fieldRef]
      if (field) this.$set(this.numberFieldTouched, field, true)
    },
    handleNumberKeyboardShown () {
      this.$nextTick(() => {
        window.setTimeout(() => this.scrollNumberFieldIntoView(), 30)
      })
    },
    scrollNumberFieldIntoView () {
      const fieldComponent = this.$refs[this.activeNumberFieldRef]
      const element = fieldComponent && fieldComponent.$el ? fieldComponent.$el : fieldComponent
      if (!element || typeof element.getBoundingClientRect !== 'function') return
      const keyboard = document.querySelector('.van-number-keyboard')
      const keyboardRect = keyboard && keyboard.getBoundingClientRect ? keyboard.getBoundingClientRect() : null
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0
      const keyboardTop = keyboardRect && keyboardRect.top > 0 && keyboardRect.top < viewportHeight
        ? keyboardRect.top
        : viewportHeight - 300
      const fieldRect = element.getBoundingClientRect()
      const visibleTop = 76
      const visibleBottom = keyboardTop - 18
      let offset = 0
      if (fieldRect.bottom > visibleBottom) {
        offset = fieldRect.bottom - visibleBottom
      } else if (fieldRect.top < visibleTop) {
        offset = fieldRect.top - visibleTop
      }
      if (!offset) return
      try {
        window.scrollBy({ top: offset, left: 0, behavior: 'smooth' })
      } catch (error) {
        window.scrollBy(0, offset)
      }
    },
    async waitForEntryTicket () {
      let query = this.readRouteQuery()
      if (query.ticket) {
        return query
      }
      const maxWaitMs = 2500
      const stepMs = 250
      const totalSteps = Math.floor(maxWaitMs / stepMs)
      for (let i = 0; i < totalSteps; i++) {
        await new Promise(resolve => window.setTimeout(resolve, stepMs))
        query = this.readRouteQuery()
        if (query.ticket) {
          console.info('[wpc-h5] delayed ticket detected', {
            waitMs: (i + 1) * stepMs,
            envKey: query.envKey || ''
          })
          return query
        }
      }
      return query
    },
    async bootstrap (reason) {
      if (this.bootstrapping) {
        return
      }
      this.bootstrapping = true
      this.loading = true
      this.authBlocked = false
      this.authErrorMessage = ''

      let query = this.readRouteQuery()
      try {
        if (!query.ticket) {
          query = await this.waitForEntryTicket()
        }

        console.info('[wpc-h5] bootstrap start', {
          reason: reason || 'created',
          hasTicket: !!query.ticket,
          envKey: query.envKey || '',
          hasCachedToken: !!getToken()
        })

        // 核心初始化只保留登录态和个人资料。
        // 微信 SDK、定位、职业列表都不能再阻塞问卷首屏。
        try {
          await this.ensureTicketSession(query)
        } catch (error) {
          this.blockByAuth(this.resolveBackendErrorMessage(error) || '登录失败，请重新进入页面')
          return
        }

        if (this.authBlocked) {
          return
        }

        try {
          // newTest=1 时 loadPatientIfNeeded() 会直接返回 false，
          // 因此只会填充 ticket 鉴权会话中的 trueName / phone / idCard。
          const loadedFromDb = await this.loadPatientIfNeeded()
          if (!loadedFromDb) {
            this.applyAuthIdentity()
          }
          if (loadedFromDb && query.pid && this.code === String(query.pid).trim() && !this.validateBeforeSubmit().message) {
            try {
              if (await this.redirectCompletedEntry(query)) return
            } catch (error) {
              console.warn('[wpc-h5] completed entry lookup failed', error)
            }
          }
        } catch (error) {
          // 非新筛查场景读取既有病例失败时，仍允许使用当前 ticket 身份信息进入页面。
          console.warn('[wpc-h5] patient preload failed, fallback to auth identity', error)
          this.applyAuthIdentity()
        }

        if (this.entrySection === 'health') {
          this.currentStep = 1
        }
      } finally {
        this.loading = false
        this.bootstrapping = false
      }

      // 非关键能力异步初始化，不再让“页面初始化中”被微信回调或慢接口卡住。
      this.initRuntimeServices(reason).catch(error => {
        console.warn('[wpc-h5] runtime services init failed', error)
      })
    },
    async initRuntimeServices (reason) {
      console.info('[wpc-h5] runtime services init start', {
        reason: reason || 'created'
      })

      // 职业列表独立加载，失败不影响页面其它输入。
      this.loadOccupationOptions().catch(error => {
        console.warn('[wpc-h5] occupation options load failed', error)
      })

      await this.detectRuntimeEnv()

      // 小程序如果已经通过 web-view URL 把经纬度带进来，直接优先使用。
      if (this.latitude && this.longitude) {
        this.resolveSiteByLocation().catch(error => {
          console.warn('[wpc-h5] site resolve by entry location failed', error)
        })
      }

      await this.locateWithFallback()
    },
    async locateWithFallback () {
      if (this._locationFallbackPromise) return this._locationFallbackPromise
      this._locationFallbackPromise = (async () => {
        const ready = this.isWechatBrowser ? await this.initWechatJsSdk() : false
        if (this.latitude && this.longitude) return
        let located = ready ? await this.requestWechatLocation() : false
        if (!located) located = await this.requestBrowserLocation()
        if (located) await this.resolveSiteByLocation()
      })()
      try {
        await this._locationFallbackPromise
      } finally {
        this._locationFallbackPromise = null
      }
    },
    readRouteQuery () {
      const routeQuery = this.$route.query || {}
      let normalQuery = {}
      let hashQuery = {}
      try {
        const currentUrl = new URL(window.location.href || '')
        normalQuery = parseQueryString(currentUrl.search)
        hashQuery = parseQueryString(currentUrl.hash.indexOf('?') > -1 ? currentUrl.hash.substring(currentUrl.hash.indexOf('?')) : '')
      } catch (e) {
        const hash = window.location.hash || ''
        hashQuery = hash.indexOf('?') > -1 ? parseQueryString(hash.substring(hash.indexOf('?'))) : {}
      }
      const query = mergeQueryObjects(normalQuery, hashQuery, routeQuery)
      if (query.patientId) {
        this.patientId = query.patientId
      }
      this.createNew = query.mode === 'create' || String(query.newTest || '') === '1'
      this.returnTo = query.returnTo || ''
      this.envKey = query.envKey || ''
      this.entrySection = query.section === 'health' ? 'health' : 'basic'
      const entryCheckId = String(query.pid || query.checkId || query.code || '').trim()
      if (entryCheckId && entryCheckId !== this.entryCheckId) {
        this.code = entryCheckId
        this.scanResultText = this.code
      }
      this.entryCheckId = entryCheckId
      this.applyLocation(
        query.latitude || query.lat || '',
        query.longitude || query.lng || '',
        'miniProgramQuery'
      )
      return query
    },
    applyLocation (latitude, longitude, source, accuracy) {
      const normalizedLatitude = normalizeCoordinate(latitude, -90, 90)
      const normalizedLongitude = normalizeCoordinate(longitude, -180, 180)
      if (!normalizedLatitude || !normalizedLongitude) return false
      this.latitude = normalizedLatitude
      this.longitude = normalizedLongitude
      this.accuracy = normalizeDigits(accuracy, 6)
      console.info('[wpc-h5] location acquired', {
        runtimeEnv: this.runtimeEnv,
        source: source || '',
        latitude: this.latitude,
        longitude: this.longitude,
        accuracy: this.accuracy
      })
      return true
    },
    requestWechatLocation () {
      if ((this.latitude && this.longitude) || this.locationRequested) return Promise.resolve(false)
      if (!this.wxReady || !window.wx || typeof window.wx.getLocation !== 'function') return Promise.resolve(false)
      this.locationRequested = true
      return new Promise((resolve) => {
        let finished = false
        let timer = null
        const finish = result => {
          if (finished) return
          finished = true
          if (timer) {
            window.clearTimeout(timer)
            timer = null
          }
          resolve(result)
        }

        timer = window.setTimeout(() => {
          console.info('[wpc-h5] wechat location timeout', {
            runtimeEnv: this.runtimeEnv
          })
          finish(false)
        }, 8000)

        try {
          window.wx.getLocation({
            type: 'wgs84',
            success: (res) => {
              if (finished) return
              finish(this.applyLocation(res.latitude, res.longitude, this.runtimeEnv || 'wechat', res.accuracy))
            },
            cancel: () => {
              finish(false)
            },
            fail: (error) => {
              console.info('[wpc-h5] wechat location unavailable', {
                runtimeEnv: this.runtimeEnv,
                message: error && error.errMsg ? error.errMsg : ''
              })
              finish(false)
            }
          })
        } catch (error) {
          console.warn('[wpc-h5] wechat location exception', error)
          finish(false)
        }
      })
    },
    requestBrowserLocation () {
      if ((this.latitude && this.longitude) || this.browserLocationRequested) return Promise.resolve(false)
      if (!window.navigator.geolocation || typeof window.navigator.geolocation.getCurrentPosition !== 'function') {
        console.info('[wpc-h5] browser geolocation is unavailable')
        return Promise.resolve(false)
      }
      this.browserLocationRequested = true
      return new Promise((resolve) => {
        let finished = false
        const finish = result => {
          if (finished) return
          finished = true
          window.clearTimeout(timer)
          resolve(result)
        }
        const timer = window.setTimeout(() => finish(false), 15000)
        try {
          window.navigator.geolocation.getCurrentPosition(
            (position) => {
              if (finished) return
              const coords = position && position.coords ? position.coords : {}
              finish(this.applyLocation(coords.latitude, coords.longitude, 'browser', coords.accuracy))
            },
            (error) => {
              console.info('[wpc-h5] browser location unavailable', {
                code: error && error.code ? error.code : ''
              })
              finish(false)
            },
            {
              enableHighAccuracy: true,
              timeout: 8000,
              maximumAge: 300000
            }
          )
        } catch (error) {
          console.info('[wpc-h5] browser location exception', error)
          finish(false)
        }
      })
    },
    async ensureTicketSession (query) {
      const currentQuery = query || this.readRouteQuery()
      const incomingTicket = currentQuery.ticket || ''
      const authMeta = getAuthMeta()
      this.authMode = authMeta.authMode || ''
      this.externalUserId = authMeta.externalUserId || ''
      this.loginTicket = authMeta.ticket || ''

      console.info('[wpc-h5] ensureTicketSession', {
        runtimeEnv: this.runtimeEnv,
        hasIncomingTicket: !!incomingTicket,
        hasCachedToken: !!getToken(),
        hasCachedTicket: !!authMeta.ticket
      })

      if (incomingTicket && authMeta.ticket && authMeta.ticket !== incomingTicket) {
        clearAuthSession()
        this.authMode = ''
        this.externalUserId = ''
        this.loginTicket = ''
      }

      if (!incomingTicket) {
        if (getToken()) {
          return
        }
        this.blockByAuth('请先登录')
        return
      }

      if (!getToken()) {
        await this.ticketLogin(currentQuery)
        if (!getToken()) {
          this.blockByAuth('ticket换取失败，未获取到登录态')
        }
        return
      }

      if (incomingTicket && (!authMeta.ticket || authMeta.ticket !== incomingTicket)) {
        await this.ticketLogin(currentQuery)
        if (!getToken()) {
          this.blockByAuth('ticket换取失败，未获取到登录态')
        }
      }
    },
    returnToSource () {
      const target = String(this.returnTo || '')
      if (target.indexOf('/projects/') === 0 || target.indexOf('/home') === 0) {
        this.$router.replace(target)
        return
      }
      if (window.history.length > 1) {
        this.$router.back()
      } else {
        this.$router.replace('/home')
      }
    },
    async ticketLogin (query) {
      const currentQuery = query || this.readRouteQuery()
      const ticket = currentQuery.ticket || ''
      if (!ticket) {
        throw new Error('缺少ticket，无法进入页面')
      }
      const res = await this.$http.post('/h5/auth/ticketLogin', Qs.stringify({
        ticket: ticket,
        channel: currentQuery.channel || '',
        envKey: currentQuery.envKey || '',
        fromWechat: this.isWechatBrowser ? '1' : '0'
      }), {
        timeout: 15000
      })
      if (res.result !== '1') {
        throw new Error(res.desc || res.data || 'ticket换取失败')
      }
      if (!res.data || !res.data.accessToken) {
        throw new Error('ticket换取成功，但未返回accessToken')
      }
      setToken(res.data.accessToken)
      setAuthMeta({
        ticket: ticket,
        channel: currentQuery.channel || '',
        envKey: res.data.envKey || currentQuery.envKey || '',
        authMode: res.data.authMode || '',
        externalUserId: res.data.externalUserId || '',
        source: res.data.source || '',
        userId: res.data.userId || '',
        userName: res.data.userName || '',
        trueName: res.data.trueName || '',
        phone: res.data.phone || '',
        idCard: res.data.idCard || ''
      })
      this.authMode = res.data.authMode || ''
      this.externalUserId = res.data.externalUserId || ''
      this.envKey = res.data.envKey || currentQuery.envKey || ''
      this.loginTicket = ticket
      this.authBlocked = false
      this.authErrorMessage = ''
    },
    async detectRuntimeEnv () {
      try {
        this.runtimeEnv = await detectWechatRuntime()
      } catch (error) {
        this.runtimeEnv = this.isWechatBrowser ? 'wechat' : 'browser'
        console.warn('[wpc-h5] runtime detect fallback', error)
      }
      console.info('[wpc-h5] runtime detected', {
        runtimeEnv: this.runtimeEnv,
        userAgent: window.navigator.userAgent || ''
      })
      return this.runtimeEnv
    },
    blockByAuth (message) {
      clearAuthSession()
      this.authBlocked = true
      this.authErrorMessage = message || '页面加载失败'
      this.authMode = ''
      this.externalUserId = ''
      this.loginTicket = ''
    },
    resolveBackendErrorMessage (error) {
      if (!error) {
        return ''
      }
      if (error.message) {
        return error.message
      }
      const responseData = error.response && error.response.data ? error.response.data : {}
      return responseData.desc || responseData.message || responseData.msg || ''
    },
    applyAuthIdentity () {
      const authMeta = getAuthMeta()
      if (!authMeta) {
        return
      }
      if (!this.form.trueName && authMeta.trueName) {
        this.form.trueName = authMeta.trueName
      }
      if (!this.form.phone && authMeta.phone) {
        this.form.phone = authMeta.phone
      }
      if (!this.form.idNumber && authMeta.idCard) {
        this.form.idNumber = String(authMeta.idCard).trim().toUpperCase()
      }
      if (!this.form.sex && this.form.idNumber) {
        const sex = getSexFromIdNumber(this.form.idNumber)
        if (sex) {
          this.form.sex = sex
        }
      }
      if (!this.form.age && this.form.idNumber) {
        const age = getAgeFromIdNumber(this.form.idNumber)
        if (age) {
          this.form.age = age
        }
      }
    },
    async redirectCompletedEntry (query) {
      const checkId = String(query.pid || '').trim()
      let pageNum = 1
      let hasMore = !!checkId
      while (hasMore) {
        const res = await this.$http.post('/eps/getSnList', Qs.stringify({ pageNum, pageSize: 50 }), { timeout: 15000 })
        if (res.result !== '1') throw new Error(res.desc || '检测记录查询失败')
        const page = res.data || {}
        const list = Array.isArray(page.list) ? page.list : []
        const product = list.find(item => String(item.serialNum || '') === checkId && item.id && String(item.patientId || '') === String(this.patientId))
        if (product) {
          this.$router.replace({
            path: '/projects/' + encodeURIComponent(product.id),
            query: { ticket: query.ticket || this.loginTicket, envKey: query.envKey || '', channel: query.channel || '', patientId: product.patientId, serialNum: product.serialNum }
          })
          return true
        }
        hasMore = list.length > 0 && page.hasNextPage !== false && pageNum * 50 < Number(page.total || 0)
        pageNum += 1
      }
      return false
    },
    async loadPatientIfNeeded () {
      // “开始新的筛查”只使用 ticket 鉴权返回的身份信息，
      // 不读取 getLatestPatient，也不继承上一次筛查的地址、职业、身高体重等资料。
      if (this.createNew && !this.patientId && !this.code) {
        return false
      }
      if (!this.patientId && !this.code) {
        const latestRes = await this.$http.get('/h5/patient/getLatestPatient', { timeout: 15000 })
        if (latestRes.result !== '1' || !latestRes.data || !latestRes.data.ID) {
          return false
        }
        this.applyPatientInfo(latestRes.data)
        return true
      }
      const res = await this.$http.get('/h5/patient/getPatientInfoDetail', {
        params: {
          patientId: this.patientId,
          code: this.code
        },
        timeout: 15000
      })
      if (res.result !== '1' || !res.data || (!res.data.ID && !res.data.CHECK_ID && !res.data.TRUE_NAME)) {
        return false
      }
      this.applyPatientInfo(res.data)
      return true
    },
    applyPatientInfo (data) {
      if (!data) {
        return
      }
      this.patientId = data.ID || this.patientId
      this.code = data.CHECK_ID || this.code
      if (!this.scanResultText) {
        this.scanResultText = this.code || this.patientId || ''
      }
      this.form.trueName = data.TRUE_NAME || ''
      this.form.sex = data.SEX ? 'true' : 'false'
      this.form.age = data.AGE || ''
      this.form.birth = data.BIRTH_DATE || data.birthDate || ''
      this.form.phone = data.PHONE || ''
      const documentType = Number(data.DOCUMENT_TYPE)
      this.documentType = documentType >= 0 && documentType < this.documentTypeOptions.length ? documentType : 0
      this.form.idNumber = data.ID_NUMBER || ''
      this.form.regionCodes = this.buildRegionCodes(data.PROVINCE, data.CITY, data.AREA)
      this.form.regionNames = [data.PROVINCE, data.CITY, data.AREA].filter(Boolean)
      this.form.address = data.ADDRESS || ''
      this.form.height = data.HEIGHT || ''
      this.form.weight = data.WEIGHT || ''
      this.form.occupation = data.OCCUPATION || data.occupation || ''
      this.signatureUrl = data.SIGNATURE_URL || this.signatureUrl
      this.siteId = this.shouldSkipInsuranceFlow ? '' : (data.SITE_ID || data.siteId || '')
      this.latitude = this.shouldSkipInsuranceFlow ? '' : (data.LATITUDE || data.latitude || this.latitude)
      this.longitude = this.shouldSkipInsuranceFlow ? '' : (data.LONGITUDE || data.longitude || this.longitude)
      this.accuracy = this.shouldSkipInsuranceFlow ? '' : (data.LOCATION_ACCURACY || data.locationAccuracy || this.accuracy)
      this.siteLocked = !this.shouldSkipInsuranceFlow && !!this.siteId
      if (this.siteId && !this.shouldSkipInsuranceFlow) {
        this.loadInsuranceContentBySite()
      }
      if (data.RECORDS) {
        const records = JSON.parse(data.RECORDS)
        if (records.version === '1.0.0') {
          this.survey = Object.assign(createSurvey(), records)
          this.otherTumourState = this.survey.medicalHistory.otherCancerHistory ? '有' : '无'
        }
        if (!this.form.birth && records.basic && records.basic.birth && records.basic.birth.data) {
          this.form.birth = records.basic.birth.data
        }
        if (!this.form.occupation && records.basic && records.basic.occupation && records.basic.occupation.data) {
          this.form.occupation = records.basic.occupation.data
        }
        if (!this.form.birth && records.entryBasicInfo && records.entryBasicInfo.birth) {
          this.form.birth = records.entryBasicInfo.birth
        }
        if (!this.form.occupation && records.entryBasicInfo && records.entryBasicInfo.occupation) {
          this.form.occupation = records.entryBasicInfo.occupation
        }
      }
    },
    async loadOccupationOptions () {
      const res = await this.$http.get('/patient/getOccupationList', { timeout: 12000 })
      if (res.result === '1' && Array.isArray(res.data)) {
        this.occupationOptions = res.data
      }
    },
    resetSiteMatchState () {
      if (this.siteLocked && this.siteId) {
        return
      }
      this.siteId = ''
      this.insuranceHtml = ''
      this.insuranceEnabled = !this.shouldSkipInsuranceFlow
      this.signatureDirty = false
      this.signatureHtmlPreview = ''
      this.signatureComposeBaseImageUrl = ''
      this.signatureComposeEnabled = false
      this.signatureDisplayImageUrl = ''
    },
    async resolveSurveySite (payload) {
      if (this.shouldSkipInsuranceFlow) {
        this.siteLocked = false
        this.siteId = ''
        this.insuranceHtml = ''
        this.insuranceEnabled = false
        return false
      }
      if (this.siteLocked && this.siteId) {
        return true
      }
      this.siteResolving = true
      try {
        const res = await this.$http.get('/patient/resolveSurveySite', { params: payload })
        if (this.siteLocked && this.siteId) {
          return true
        }
        if (res.result !== '1' || !res.data) {
          throw new Error(res.desc || res.data || '站点识别失败')
        }
        this.siteId = res.data.siteId || ''
        this.insuranceHtml = res.data.insuranceHtml || res.data.INSURANCE_HTML || ''
        this.insuranceEnabled = true
        this.applyInsuranceContent(res.data)
        if (this.siteId) {
          this.siteLocked = true
        }
        if (!this.insuranceHtml && !this.signatureDisplayImageUrl) {
          await this.loadInsuranceContentBySite()
        }
        this.$nextTick(() => {
          if (this.showInsuranceModal && !this.signatureUrl) {
            this.initSignatureCanvas()
          }
        })
        return true
      } catch (error) {
        this.resetSiteMatchState()
        return false
      } finally {
        this.siteResolving = false
      }
    },
    async loadInsuranceContentBySite () {
      if (!this.siteId) {
        this.insuranceHtml = ''
        this.insuranceEnabled = !this.shouldSkipInsuranceFlow
        return false
      }
      try {
        const res = await this.$http.get('/patient/getSurveyInsuranceContent', {
          params: {
            siteId: this.siteId,
            _ts: Date.now()
          }
        })
        if (res.result === '1' && res.data) {
          this.insuranceEnabled = true
          this.insuranceHtml = res.data.insuranceHtml || res.data.INSURANCE_HTML || ''
          this.applyInsuranceContent(res.data)
          return true
        }
      } catch (error) {
      }
      this.insuranceHtml = ''
      this.signatureHtmlPreview = ''
      this.signatureComposeBaseImageUrl = ''
      this.signatureComposeEnabled = false
      this.signatureDisplayImageUrl = ''
      this.insuranceEnabled = !this.shouldSkipInsuranceFlow
      return false
    },
    async resolveSiteByLocation () {
      if (this.shouldSkipInsuranceFlow) {
        this.siteLocked = false
        this.siteId = ''
        this.insuranceHtml = ''
        this.insuranceEnabled = false
        return false
      }
      if (!this.latitude || !this.longitude) {
        return false
      }
      return this.resolveSurveySite({
        latitude: this.latitude,
        longitude: this.longitude,
        accuracy: this.accuracy
      })
    },
    async retrySiteResolve () {
      if (this.siteLocked && this.siteId) {
        return true
      }
      this.locationRequested = false
      this.browserLocationRequested = false
      if (this.isWechatBrowser) {
        await this.requestWechatLocation()
      } else {
        await this.requestBrowserLocation()
      }
      return this.resolveSiteByLocation()
    },
    buildRegionPayload () {
      const region = (this.form.regionNames || []).filter(Boolean)
      return {
        province: region[0] || '',
        city: region[1] || '',
        area: region[2] || ''
      }
    },
    buildRegionCodes (province, city, area) {
      if (!province) {
        return []
      }
      const provinceNode = this.regionOptions.find(item => item.label === province)
      if (!provinceNode) {
        return []
      }
      if (!city || !provinceNode.children || provinceNode.children.length === 0) {
        return [provinceNode.value]
      }
      const cityNode = provinceNode.children.find(item => item.label === city)
      if (!cityNode) {
        return [provinceNode.value]
      }
      if (!area || !cityNode.children || cityNode.children.length === 0) {
        return [provinceNode.value, cityNode.value]
      }
      const areaNode = cityNode.children.find(item => item.label === area)
      if (!areaNode) {
        return [provinceNode.value, cityNode.value]
      }
      return [provinceNode.value, cityNode.value, areaNode.value]
    },
    handleSexConfirm (value) {
      this.form.sex = value === '女' ? 'false' : 'true'
      this.showSexPicker = false
    },
    handleDocumentTypeConfirm (value, index) {
      this.documentType = Number(index) || 0
      this.idNumberSystemKeyboard = false
      this.numberKeyboardVisible = false
      this.$set(this.numberFieldTouched, 'idNumber', false)
      this.showDocumentTypePicker = false
    },
    handleBirthConfirm (value) {
      this.form.birth = formatDateValue(value)
      this.showBirthPicker = false
    },
    handleOccupationConfirm (value) {
      this.form.occupation = value || ''
      this.showOccupationPicker = false
    },
    handleRegionConfirm (values) {
      const selected = Array.isArray(values) ? values : []
      this.form.regionCodes = selected.map(item => item.code)
      this.form.regionNames = selected.map(item => item.name)
      this.showRegionPicker = false
    },
    handleSymptomsChange (value) {
      this.survey.symptoms = resolveExclusiveSelection(value, this.survey.symptoms, '无症状')
    },
    handleDigestiveChange (value) {
      this.survey.medicalHistory.digestiveSystem = resolveExclusiveSelection(value, this.survey.medicalHistory.digestiveSystem, '无消化系统疾病史')
    },
    handleLifestyleChange (value) {
      this.survey.unhealthyLifestyles = resolveExclusiveSelection(value, this.survey.unhealthyLifestyles, '无')
    },
    validateBasicInfo () {
      const regionPayload = this.buildRegionPayload()
      const currentCheckId = String(this.code || '').trim()
      if (!currentCheckId) {
        return { message: '请先扫描或输入正确的检查单二维码内容', field: 'checkIdField' }
      }
      if (!this.form.trueName) {
        return { message: '请输入姓名', field: 'trueNameField' }
      }
      if (!this.form.sex) {
        return { message: '请选择性别', field: 'sexField' }
      }
      const age = Number(this.form.age)
      if (!isPositiveNumber(this.form.age)) {
        return { message: '请输入年龄', field: 'ageField' }
      }
      if (!/^\d+$/.test(String(this.form.age)) || age < 16 || age > 120) {
        return { message: '年龄应在16至120岁之间', field: 'ageField' }
      }
      if (!this.form.birth) {
        return { message: '请选择出生日期', field: 'birthField' }
      }
      if (!this.form.occupation) {
        return { message: '请选择职业', field: 'occupationField' }
      }
      if (!this.form.phone) {
        return { message: '请输入联系电话', field: 'phoneField' }
      }
      if (!/^1\d{10}$/.test(String(this.form.phone).trim())) {
        return { message: '请输入正确的手机号', field: 'phoneField' }
      }
      if (!this.form.idNumber) {
        return { message: '请输入证件号码', field: 'idNumberField' }
      }
      if (this.documentType === 0 && !isValidIdNumber(this.form.idNumber)) {
        return { message: '请输入正确的身份证号', field: 'idNumberField' }
      }
      if (this.documentType !== 0 && !/^[0-9a-zA-Z]+$/.test(String(this.form.idNumber).trim())) {
        return { message: '请输入正确的证件号码', field: 'idNumberField' }
      }
      if (!regionPayload.province || !regionPayload.city || !regionPayload.area) {
        return { message: '请选择现居住地', field: 'regionField' }
      }
      if (!this.form.address) {
        return { message: '请输入详细地址', field: 'addressField' }
      }
      if (this.showSignatureEntry && !this.signatureUrl) {
        return { message: '请先完成知情同意说明书签署', field: 'signatureField' }
      }
      if (!isPositiveNumber(this.form.height)) {
        return { message: '请输入身高', field: 'heightField' }
      }
      const height = Number(this.form.height)
      if (height < 80 || height > 250) {
        return { message: '身高应在80至250厘米之间', field: 'heightField' }
      }
      if (!isPositiveNumber(this.form.weight)) {
        return { message: '请输入体重', field: 'weightField' }
      }
      const weight = Number(this.form.weight)
      if (weight < 20 || weight > 300) {
        return { message: '体重应在20至300公斤之间', field: 'weightField' }
      }
      return { message: '', field: '' }
    },
    validateBeforeSubmit () {
      const basicValidation = this.validateBasicInfo()
      if (basicValidation.message) {
        return basicValidation
      }
      if (!this.survey.medicalHistory.digestiveSystem.length) {
        return { message: '请选择消化系统疾病史', field: 'digestiveField' }
      }
      if (this.showOtherDigestive && !this.survey.medicalHistory.otherSymptomInfo) {
        return { message: '请填写其他消化道病史', field: 'otherDigestiveField' }
      }
      if (!this.survey.medicalHistory.headAndNeck) {
        return { message: '请选择头颈部情况', field: 'headNeckField' }
      }
      if (this.otherTumourState === '有' && !this.survey.medicalHistory.otherCancerHistory) {
        return { message: '请填写其他肿瘤病史', field: 'headNeckField' }
      }
      if (!this.survey.oralHealth.noMissingTeeth) {
        return { message: '请选择是否缺齿', field: 'oralHealthField' }
      }
      if (this.survey.oralHealth.noMissingTeeth === '是' && !isPositiveNumber(this.survey.oralHealth.missingTeeth)) {
        return { message: '请填写缺齿数', field: 'missingTeethField' }
      }
      if (!this.survey.unhealthyLifestyles.length) {
        return { message: '请选择不良生活习惯', field: 'lifestyleField' }
      }
      if (!this.survey.familyHistory.first.has) {
        return { message: '请选择一级亲属食管癌情况', field: 'familyFirstField' }
      }
      if (this.survey.familyHistory.first.has === '是' &&
        (!this.survey.familyHistory.first.details || !isPositiveNumber(this.survey.familyHistory.first.number))) {
        return { message: '请填写一级亲属食管癌具体关系和人数', field: 'familyFirstDetailField' }
      }
      if (!this.survey.familyHistory.second.has) {
        return { message: '请选择二级亲属食管癌情况', field: 'familySecondField' }
      }
      if (this.survey.familyHistory.second.has === '是' &&
        (!this.survey.familyHistory.second.details || !isPositiveNumber(this.survey.familyHistory.second.number))) {
        return { message: '请填写二级亲属食管癌具体关系和人数', field: 'familySecondDetailField' }
      }
      if (!this.survey.consentAgreed) {
        return { message: '请阅读并勾选知情同意书', field: 'consentField' }
      }
      return { message: '', field: '' }
    },
    goNextStep () {
      const validation = this.validateBasicInfo()
      if (validation.message) {
        this.markNumberFieldTouchedByRef(validation.field)
        this.$Message.warning(validation.message)
        this.scrollToField(validation.field)
        return
      }
      this.currentStep = 1
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    scrollToField (fieldRef) {
      if (!fieldRef) {
        return
      }
      this.$nextTick(() => {
        const ref = this.$refs[fieldRef]
        const target = Array.isArray(ref) ? ref[0] : ref
        const el = target && target.$el ? target.$el : target
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      })
    },
    buildSubmitPayload () {
      const regionPayload = this.buildRegionPayload()
      return {
        isEdit: this.patientId ? 1 : 0,
        patientId: this.patientId,
        code: this.code,
        trueName: this.form.trueName,
        age: this.form.age,
        birth: this.form.birth,
        sex: this.form.sex,
        phone: this.form.phone,
        occupation: this.form.occupation,
        documentType: this.documentType,
        idNumber: String(this.form.idNumber || '').trim().toUpperCase(),
        province: regionPayload.province,
        city: regionPayload.city,
        area: regionPayload.area,
        address: this.form.address,
        height: this.form.height,
        weight: this.form.weight,
        siteId: this.shouldSkipInsuranceFlow ? '' : this.siteId,
        accuracy: this.shouldSkipInsuranceFlow ? '' : this.accuracy,
        signatureUrl: this.signatureUrl,
        latitude: this.shouldSkipInsuranceFlow ? '' : this.latitude,
        longitude: this.shouldSkipInsuranceFlow ? '' : this.longitude,
        records: JSON.stringify(this.survey)
      }
    },
    buildPatientSubmitPayload (payload) {
      return {
        isEdit: payload.isEdit,
        patientId: payload.patientId,
        code: payload.code,
        tijianCode: '',
        trueName: payload.trueName,
        age: payload.age,
        birth: payload.birth,
        sex: payload.sex,
        phone: payload.phone,
        occupation: payload.occupation,
        documentType: payload.documentType,
        idNumber: payload.idNumber,
        province: payload.province,
        city: payload.city,
        area: payload.area,
        address: payload.address,
        signatureUrl: payload.signatureUrl,
        idCardUrl: '',
        envKey: this.envKey || '',
        siteId: payload.siteId,
        accuracy: payload.accuracy,
        latitude: payload.latitude,
        longitude: payload.longitude
      }
    },
    async submitByMiniProgramApis (payload) {
      const patientRes = await this.$http.get('/patient/submitPatInfo', {
        params: this.buildPatientSubmitPayload(payload)
      })
      if (patientRes.result !== '1') {
        throw new Error(patientRes.desc || patientRes.data || '检测人信息提交失败')
      }
      const patientId = patientRes.data || payload.patientId
      if (!patientId) {
        throw new Error('检测人信息提交成功，但未返回检测人ID')
      }

      const recordsRes = await this.$http.post('/patient/saveHealthRecords', Qs.stringify({
        patientId: patientId,
        height: payload.height,
        weight: payload.weight,
        records: payload.records
      }))
      if (recordsRes.result !== '1') {
        throw new Error(recordsRes.desc || recordsRes.data || '健康信息提交失败')
      }
      return patientId
    },
    getSubmitSignature (payload) {
      return JSON.stringify(payload || {})
    },
    showWechatError (reason) {
      console.warn('[wpc-h5] scan unavailable', reason)
      this.$Message.warning('扫码暂不可用，请稍后重试')
    },
    async initWechatJsSdk () {
      if (needsParentWechat()) return false
      if (!this.isWechatBrowser) {
        return false
      }
      if (this.wxReady && window.wx) {
        return true
      }
      if (this._wechatInitPromise) {
        return this._wechatInitPromise
      }

      this._wechatInitPromise = (async () => {
        try {
          const result = await configWechatJssdk(this.$http, {
            wxId: 10,
            jsApiList: ['scanQRCode', 'getLocation']
          })
          this.wxReady = !!(result && result.ready)
          this.wxInitError = this.wxReady ? '' : ((result && result.reason) || '微信未返回具体错误信息')
          if (!this.wxReady) {
            console.warn('[wpc-h5] wechat jssdk unavailable', {
              runtimeEnv: this.runtimeEnv,
              reason: result && result.reason ? result.reason : ''
            })
          }
          return this.wxReady
        } catch (error) {
          this.wxReady = false
          this.wxInitError = (error && error.message) || '微信 JS-SDK 初始化异常'
          console.warn('[wpc-h5] wechat jssdk init failed', {
            runtimeEnv: this.runtimeEnv,
            message: error && error.message ? error.message : ''
          })
          return false
        } finally {
          this._wechatInitPromise = null
        }
      })()

      return this._wechatInitPromise
    },
    async scanByWechat () {
      const now = Date.now()
      if (this._wechatScanPending &&
          (!needsParentWechat() || now - this._wechatScanStartedAt < 1000)) return
      this._wechatScanStartedAt = now
      const attempt = (this._wechatScanAttempt || 0) + 1
      this._wechatScanAttempt = attempt
      this._wechatScanPending = true
      try {
        await this.performWechatScan()
      } finally {
        if (this._wechatScanAttempt === attempt) this._wechatScanPending = false
      }
    },
    async performWechatScan () {
      if (this.hasLockedCheckId) {
        this.$Message.warning('保存成功后，检查单号不允许再修改')
        return
      }

      if (needsParentWechat()) {
        try {
          const result = await scanByParent()
          if (result) await this.applyScannedResult(result)
        } catch (error) {
          this.showWechatError(error.message || '外层页面扫码失败')
        }
        return
      }

      if (!this.wxReady || !window.wx) {
        const ready = await this.initWechatJsSdk()
        if (!ready || !window.wx || typeof window.wx.scanQRCode !== 'function') {
          this.showWechatError(this.wxInitError || '微信 SDK 未提供 scanQRCode 接口')
          return
        }
      }

      try {
        window.wx.scanQRCode({
          needResult: 1,
          scanType: ['qrCode'],
          success: (res) => {
            const result = res.resultStr || ''
            this.applyScannedResult(result)
          },
          fail: (error) => {
            console.warn('[wpc-h5] scan qrcode failed', error)
            this.showWechatError((error && error.errMsg) || '扫码失败，微信未返回具体错误信息')
          }
        })
      } catch (error) {
        console.warn('[wpc-h5] scan qrcode exception', error)
        this.showWechatError((error && error.message) || '扫码能力调用异常')
      }
    },
    syncManualCheckId () {
      if (this.isWechatBrowser) {
        return
      }
      const parsed = parseCheckEntryCodeFromUrl(this.code)
      if (!parsed.code) {
        this.scanResultText = ''
        if (String(this.code || '').trim()) {
          this.$Message.warning(parsed.message)
        }
        return
      }
      this.code = parsed.code
      this.scanResultText = parsed.code
    },
    async applyScannedResult (value) {
      if (!value) {
        return
      }
      const normalizedValue = normalizeScannedValue(value)
      const parsed = parseCheckEntryCodeFromUrl(normalizedValue)
      if (!parsed.code) {
        this.$Message.warning(parsed.message)
        return
      }
      this.code = parsed.code
      this.scanResultText = parsed.code
    },
    confirmConsent () {
      this.survey.consentAgreed = true
      this.showConsentModal = false
    },
    async openInsuranceModal () {
      if (this.shouldSkipInsuranceFlow) {
        this.prepareLegacyCzSignature()
        this.showInsuranceModal = true
        this.$nextTick(() => {
          if (!this.signatureUrl) {
            this.initSignatureCanvas()
          }
        })
        return
      }
      if (this.siteId && !this.insuranceHtml) {
        await this.loadInsuranceContentBySite()
      }
      if (this.insuranceHtml || this.signatureDisplayImageUrl) {
        this.signaturePageMode = 'insurance'
      } else {
        this.prepareDefaultConsentSignature()
      }
      this.showInsuranceModal = true
      this.$nextTick(() => {
        if (!this.signatureUrl) {
          this.initSignatureCanvas()
        }
      })
    },
    closeSignaturePage () {
      this.showInsuranceModal = false
      this.signatureDrawing = false
    },
    prepareLegacyCzSignature () {
      this.signaturePageMode = 'cz'
      this.signatureHtmlPreview = ''
      this.signatureDisplayImageUrl = this.appendCacheBuster(LEGACY_CZ_SIGNATURE_IMAGE)
      this.signatureComposeBaseImageUrl = this.signatureDisplayImageUrl
      this.signatureComposeEnabled = true
      this.signatureComposeConfig = Object.assign({}, DEFAULT_SIGNATURE_COMPOSE_CONFIG)
    },
    prepareDefaultConsentSignature () {
      this.signaturePageMode = 'default'
      this.signatureHtmlPreview = DEFAULT_CONSENT_HTML
      this.signatureDisplayImageUrl = this.appendCacheBuster(DEFAULT_INSURANCE_IMAGE_URL)
      this.signatureComposeBaseImageUrl = this.signatureDisplayImageUrl
      this.signatureComposeEnabled = true
      this.signatureComposeConfig = Object.assign({}, DEFAULT_SIGNATURE_COMPOSE_CONFIG)
    },
    applyInsuranceContent (data) {
      const payload = data || {}
      const html = payload.insuranceHtml || payload.INSURANCE_HTML || ''
      const imageUrl = this.appendCacheBuster(DEFAULT_INSURANCE_IMAGE_URL)
      const htmlPreview = this.appendCacheBusterToHtmlImages(html)
      const fallbackImage = this.extractFirstImageFromHtml(html)
      this.signaturePageMode = 'insurance'
      this.signatureHtmlPreview = htmlPreview
      this.signatureDisplayImageUrl = imageUrl || fallbackImage || ''
      this.signatureComposeBaseImageUrl = imageUrl || fallbackImage || ''
      this.signatureComposeEnabled = !!this.signatureComposeBaseImageUrl
      this.signatureComposeConfig = {
        signXRatio: DEFAULT_SIGNATURE_COMPOSE_CONFIG.signXRatio,
        signYRatio: DEFAULT_SIGNATURE_COMPOSE_CONFIG.signYRatio,
        signWidthRatio: DEFAULT_SIGNATURE_COMPOSE_CONFIG.signWidthRatio,
        signHeightRatio: DEFAULT_SIGNATURE_COMPOSE_CONFIG.signHeightRatio,
        dateXRatio: DEFAULT_SIGNATURE_COMPOSE_CONFIG.dateXRatio,
        dateYRatio: DEFAULT_SIGNATURE_COMPOSE_CONFIG.dateYRatio,
        dateFontSize: DEFAULT_SIGNATURE_COMPOSE_CONFIG.dateFontSize
      }
    },
    appendCacheBuster (url) {
      const source = String(url || '').trim()
      if (!source) {
        return ''
      }
      const joiner = source.indexOf('?') > -1 ? '&' : '?'
      return `${source}${joiner}_ts=${Date.now()}`
    },
    appendCacheBusterToHtmlImages (html) {
      if (!html) {
        return ''
      }
      return String(html).replace(/(<img[^>]+src=['"])([^'"]+)(['"][^>]*>)/ig, (match, prefix, src, suffix) => {
        return `${prefix}${this.appendCacheBuster(src)}${suffix}`
      })
    },
    extractFirstImageFromHtml (html) {
      if (!html) {
        return ''
      }
      const imageMatch = String(html).match(/<img[^>]+src=['"]([^'"]+)['"]/i)
      return imageMatch && imageMatch[1] ? this.appendCacheBuster(imageMatch[1]) : ''
    },
    normalizeRatio (value, defaultValue) {
      const num = Number(value)
      if (Number.isNaN(num) || num <= 0 || num > 1) {
        return defaultValue
      }
      return num
    },
    normalizeInteger (value, defaultValue) {
      const num = parseInt(value, 10)
      if (Number.isNaN(num) || num <= 0) {
        return defaultValue
      }
      return num
    },
    initSignatureCanvas () {
      this.$nextTick(() => {
        const canvas = this.$refs.signatureCanvas
        if (!canvas) return
        const ratio = Math.max(window.devicePixelRatio || 1, 1)
        const rectWidth = canvas.clientWidth || 280
        const rectHeight = canvas.clientHeight || 160
        canvas.width = Math.floor(rectWidth * ratio)
        canvas.height = Math.floor(rectHeight * ratio)
        const context = canvas.getContext('2d')
        if (!context) return
        context.setTransform(ratio, 0, 0, ratio, 0, 0)
        context.clearRect(0, 0, rectWidth, rectHeight)
        context.fillStyle = '#ffffff'
        context.fillRect(0, 0, rectWidth, rectHeight)
        context.lineWidth = 2
        context.lineCap = 'round'
        context.lineJoin = 'round'
        context.strokeStyle = '#1f2937'
      })
    },
    getSignaturePoint (event) {
      const canvas = this.$refs.signatureCanvas
      if (!canvas) return null
      const rect = canvas.getBoundingClientRect()
      const source = event.touches && event.touches[0] ? event.touches[0] : event
      return {
        x: source.clientX - rect.left,
        y: source.clientY - rect.top
      }
    },
    startSignature (event) {
      if (this.signatureUrl) return
      const point = this.getSignaturePoint(event)
      const canvas = this.$refs.signatureCanvas
      if (!point || !canvas) return
      const context = canvas.getContext('2d')
      if (!context) return
      context.beginPath()
      context.moveTo(point.x, point.y)
      this.signatureDrawing = true
      this.signatureDirty = true
    },
    moveSignature (event) {
      if (!this.signatureDrawing || this.signatureUrl) return
      const point = this.getSignaturePoint(event)
      const canvas = this.$refs.signatureCanvas
      if (!point || !canvas) return
      const context = canvas.getContext('2d')
      if (!context) return
      context.lineTo(point.x, point.y)
      context.stroke()
    },
    endSignature () {
      this.signatureDrawing = false
    },
    clearSignature () {
      this.signatureUrl = ''
      this.signatureDirty = false
      this.initSignatureCanvas()
    },
    loadImage (source) {
      return new Promise((resolve, reject) => {
        const image = new window.Image()
        image.crossOrigin = 'anonymous'
        image.onload = () => resolve(image)
        image.onerror = () => reject(new Error('图片加载失败'))
        image.src = source
      })
    },
    createCanvasBlob (canvas, mimeType) {
      return new Promise((resolve, reject) => {
        canvas.toBlob((result) => {
          if (result) {
            resolve(result)
            return
          }
          reject(new Error('图片生成失败'))
        }, mimeType || 'image/png')
      })
    },
    getComposeDateText () {
      const date = new Date()
      return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`
    },
    async buildUploadSignatureBlob () {
      const canvas = this.$refs.signatureCanvas
      if (!canvas || typeof canvas.toBlob !== 'function') {
        throw new Error('当前浏览器暂不支持签名上传')
      }
      const signatureBlob = await this.createCanvasBlob(canvas, 'image/png')
      if (!this.signatureComposeEnabled || !this.signatureComposeBaseImageUrl) {
        return {
          blob: signatureBlob,
          fileName: 'signature.png'
        }
      }
      const baseImage = await this.loadImage(this.signatureComposeBaseImageUrl)
      const signatureImage = await this.loadImage(window.URL.createObjectURL(signatureBlob))
      try {
        const composeCanvas = document.createElement('canvas')
        composeCanvas.width = baseImage.naturalWidth || baseImage.width
        composeCanvas.height = baseImage.naturalHeight || baseImage.height
        const context = composeCanvas.getContext('2d')
        if (!context) {
          throw new Error('签名合成失败')
        }
        context.fillStyle = '#ffffff'
        context.fillRect(0, 0, composeCanvas.width, composeCanvas.height)
        context.drawImage(baseImage, 0, 0, composeCanvas.width, composeCanvas.height)
        context.fillStyle = '#111111'
        context.font = `${this.signatureComposeConfig.dateFontSize}px sans-serif`
        context.textBaseline = 'alphabetic'
        context.fillText(
          this.getComposeDateText(),
          composeCanvas.width * this.signatureComposeConfig.dateXRatio,
          composeCanvas.height * this.signatureComposeConfig.dateYRatio
        )
        context.drawImage(
          signatureImage,
          composeCanvas.width * this.signatureComposeConfig.signXRatio,
          composeCanvas.height * this.signatureComposeConfig.signYRatio,
          composeCanvas.width * this.signatureComposeConfig.signWidthRatio,
          composeCanvas.height * this.signatureComposeConfig.signHeightRatio
        )
        const blob = await this.createCanvasBlob(composeCanvas, 'image/jpeg')
        return {
          blob: blob,
          fileName: 'signature-contract.jpg'
        }
      } finally {
        window.URL.revokeObjectURL(signatureImage.src)
      }
    },
    async uploadSignature () {
      if (this.signatureUrl) {
        return
      }
      if (!this.signatureDirty) {
        this.$Message.warning('请先完成签名')
        return
      }
      this.signatureUploading = true
      try {
        const uploadPayload = await this.buildUploadSignatureBlob()
        const formData = new window.FormData()
        formData.append('file', uploadPayload.blob, uploadPayload.fileName)
        const res = await this.$http.post('/post/uploadSignatureContract', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        const uploadedUrl = typeof res === 'string' ? res : (res.data || '')
        if (!uploadedUrl) {
          throw new Error('签名上传失败')
        }
        this.signatureUrl = uploadedUrl
        this.signatureDirty = false
      } catch (error) {
        this.$Message.error(error.message || '签名上传失败')
      } finally {
        this.signatureUploading = false
      }
    },
    async ensureTokenBeforeSubmit () {
      if (getToken()) {
        return
      }
      const currentQuery = this.readRouteQuery()
      const ticket = currentQuery.ticket || this.loginTicket || ''
      if (!ticket) {
        throw new Error('登录态已失效，请重新从小程序或微信入口进入')
      }
      await this.ticketLogin(Object.assign({}, currentQuery, {
        ticket: ticket
      }))
      if (!getToken()) {
        throw new Error('登录态恢复失败，请重新进入页面后再提交')
      }
    },
    buildReportQuery () {
      return {
        ticket: this.loginTicket || '',
        patientId: this.patientId || '',
        code: this.code || '',
        checkId: this.code || '',
        trueName: this.form.trueName || '',
        phone: this.form.phone || '',
        idNumber: this.form.idNumber || ''
      }
    },
    goToReportQuery () {
      this.$router.push({
        path: '/report',
        query: this.buildReportQuery()
      })
    },
    async submitAll () {
      this.saving = true
      try {
        const validation = this.validateBeforeSubmit()
        if (validation.message) {
          if (this.currentStep === 1 && ['checkIdField', 'trueNameField', 'sexField', 'ageField', 'phoneField', 'idNumberField', 'regionField', 'addressField', 'heightField', 'weightField'].indexOf(validation.field) > -1) {
            this.currentStep = 0
          }
          this.markNumberFieldTouchedByRef(validation.field)
          this.$Message.warning(validation.message)
          this.scrollToField(validation.field)
          return
        }
        await this.ensureTokenBeforeSubmit()
        if (!this.siteLocked && this.latitude && this.longitude) {
          await this.resolveSiteByLocation()
        }
        this.survey.medicalHistory.otherCancerHistory = this.otherTumourState === '有'
          ? this.survey.medicalHistory.otherCancerHistory
          : ''
        const payload = this.buildSubmitPayload()
        const submitSignature = this.getSubmitSignature(payload)
        if (sessionStorage.getItem(SUBMIT_DEDUP_KEY) === submitSignature) {
          this.$Message.warning('相同内容已提交，请勿重复提交')
          return
        }
        this.patientId = await this.submitByMiniProgramApis(payload)
        sessionStorage.setItem(SUBMIT_DEDUP_KEY, submitSignature)
        this.$router.replace({
          path: '/success',
          query: {
            ticket: this.loginTicket,
            patientId: this.patientId,
            code: this.code,
            checkId: this.code
          }
        })
      } catch (error) {
        this.$Message.error(error.message || '提交失败')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.relation-field { width: 100%; min-height: 44px; padding: 10px 12px; display: flex; align-items: center; justify-content: space-between; gap: 8px; text-align: left; border: 1px solid #dfe3e8; border-radius: 8px; color: #475467; background: #fff; font-size: 14px; line-height: 1.6; }
.relation-field span { flex: 1; overflow-wrap: anywhere; }
.relation-sheet { color: #183550; background: #fff; }
.relation-sheet__header { padding: 22px 20px 16px; display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 1px solid #edf2f8; }
.relation-sheet__header b { font-size: 17px; }
.relation-sheet__header p { margin-top: 6px; font-size: 12px; color: #76899d; }
.relation-sheet__header button { border: 0; background: #f2f6fa; border-radius: 50%; width: 34px; height: 34px; font-size: 25px; }
.relation-sheet__options { padding: 18px 20px; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; max-height: 48vh; overflow-y: auto; -webkit-overflow-scrolling: touch; }
.relation-option { padding: 12px; display: flex; align-items: center; gap: 10px; border: 1px solid #e0e9f2; border-radius: 10px; font-size: 14px; overflow-wrap: anywhere; cursor: pointer; }
.relation-option.selected { color: #007de9; background: #edf7ff; border-color: #007de9; }
.relation-option input { width: 18px; height: 18px; flex-shrink: 0; accent-color: #007de9; }
.relation-sheet__footer { display: flex; align-items: center; justify-content: space-between; gap: 20px; padding: 14px 20px; padding-bottom: calc(14px + constant(safe-area-inset-bottom)); padding-bottom: calc(14px + env(safe-area-inset-bottom)); border-top: 1px solid #edf2f8; }
.relation-sheet__footer span { color: #76899d; font-size: 13px; }
.relation-sheet__footer button { flex: 1; max-width: 220px; min-height: 44px; border: 0; border-radius: 10px; background: #007de9; color: #fff; font-size: 15px; }
.page {
  min-height: 100vh;
  max-width: 620px;
  margin: 0 auto;
  padding: 0 12px 104px;
  padding-bottom: calc(104px + constant(safe-area-inset-bottom));
  padding-bottom: calc(104px + env(safe-area-inset-bottom));
  box-sizing: border-box;
  background: var(--wpc-bg);
}

.page.number-keyboard-open {
  padding-bottom: 390px;
  padding-bottom: calc(390px + constant(safe-area-inset-bottom));
  padding-bottom: calc(390px + env(safe-area-inset-bottom));
}

.number-keyboard-open .action-bar {
  opacity: 0;
  pointer-events: none;
}

.context-back {
  width: 100%;
  min-height: 46px;
  padding: 0 2px;
  border: 0;
  border-bottom: 1px solid var(--wpc-line);
  display: flex;
  align-items: center;
  gap: 5px;
  color: var(--wpc-primary);
  background: transparent;
  font-size: 14px;
  text-align: left;
}

.context-back > i {
  font-size: 21px;
}

.panel {
  margin-top: 12px;
  border: 1px solid #edf0f4;
  border-radius: var(--wpc-radius);
  box-shadow: none;
}

.survey-progress {
  position: sticky;
  z-index: 10;
  top: 0;
  margin: 0 -12px;
  min-height: 62px;
  padding: 9px 42px 7px;
  background: rgba(255, 255, 255, .97);
  border-bottom: 1px solid var(--wpc-line);
  display: grid;
  grid-template-columns: 74px 1fr 74px;
  align-items: center;
}

.survey-progress > i {
  height: 2px;
  margin-bottom: 18px;
  background: #dfe5ed;
}

.survey-progress__step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  color: #9aa4b3;
}

.survey-progress__step span {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 1px solid #d5dbe4;
  display: grid;
  place-items: center;
  background: #fff;
}

.survey-progress__step b { font-size: 12px; white-space: nowrap; }
.survey-progress__step.active { color: var(--wpc-primary); }
.survey-progress__step.active span { color: #fff; border-color: var(--wpc-primary); background: var(--wpc-primary); }

.panel--error {
  border: 1px solid #f3d3d6;
}

.panel--loading {
  border: 1px solid #e6ebf5;
}

.auth-loading {
  color: #63738a;
  line-height: 1.6;
  font-size: 15px;
  text-align: center;
  padding: 24px 0;
}

.auth-error {
  color: #8c8c8c;
  line-height: 1.6;
  font-size: 15px;
  text-align: center;
  padding: 20px 0;
}

.page /deep/ .ivu-input {
  height: 44px;
  padding: 10px 12px;
  border-color: #dfe3e8;
  border-radius: 8px;
  line-height: 24px;
  font-size: 16px;
  box-sizing: border-box;
}

.page /deep/ .ivu-input-wrapper {
  display: block;
}

.page /deep/ .ivu-input-group {
  display: table;
}

.page /deep/ .ivu-input-group .ivu-input {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.page /deep/ .ivu-input-group-append {
  width: 42px;
  padding: 0 8px;
  border-color: #dfe3e8;
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
  background: #f7f9fb;
  text-align: center;
}

.number-field-active /deep/ .ivu-input,
.number-field-active /deep/ .ivu-input-group-append {
  border-color: var(--wpc-primary);
}

.number-field-active /deep/ .ivu-input {
  box-shadow: 0 0 0 2px rgba(25, 158, 216, .12);
}

.number-field-error /deep/ .ivu-input,
.number-field-error /deep/ .ivu-input-group-append {
  border-color: #ed6a6a;
}

.number-field-error-text {
  margin-top: 6px;
  color: #d94f4f;
  font-size: 12px;
  line-height: 1.4;
}

.number-field-unit {
  color: #65758b;
  font-size: 13px;
}

.page /deep/ .ivu-input[disabled],
.page /deep/ .ivu-input[readonly] {
  opacity: 1;
}

.page /deep/ .ivu-input::placeholder {
  color: #b8b8b8;
}

.mobile-trigger {
  width: 100%;
  height: 44px;
  border: 1px solid #dfe3e8;
  border-radius: 8px;
  box-sizing: border-box;
  padding: 0 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  cursor: pointer;
  font-size: 16px;
}

.site-status-card {
  padding: 12px;
  border: 1px solid #dbe7f5;
  border-radius: 10px;
  background: #f7fbff;
}

.site-status-card__line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;
}

.site-status-card__label {
  color: #667085;
}

.site-status-card__ok {
  color: #167c4f;
  font-weight: 600;
}

.site-status-card__warn {
  color: #c15b12;
  font-weight: 600;
}

.site-status-card__sub {
  margin-top: 8px;
  color: #667085;
  font-size: 12px;
  line-height: 1.6;
}

.site-status-card__actions {
  margin-top: 10px;
  display: flex;
  gap: 10px;
}

.consent-divider-link {
  color: var(--wpc-primary);
  cursor: pointer;
}

.signature-entry-card {
  padding: 12px;
  border: 1px solid #dbe1ea;
  border-radius: 10px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.signature-entry-card__text {
  min-width: 0;
}

.signature-entry-card__title {
  color: #1f2937;
  font-size: 14px;
  font-weight: 600;
}

.signature-entry-card__desc {
  margin-top: 6px;
  color: #667085;
  font-size: 12px;
  line-height: 1.6;
}

.signature-page {
  position: fixed;
  inset: 0;
  z-index: 4000;
  display: flex;
  flex-direction: column;
  background: #f7f9fc;
}

.signature-page__header {
  flex: 0 0 auto;
  min-height: 52px;
  padding: calc(8px + constant(safe-area-inset-top)) 12px 8px;
  padding: calc(8px + env(safe-area-inset-top)) 12px 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e8edf3;
  background: #ffffff;
}

.signature-page__back {
  min-width: 56px;
  border: 0;
  padding: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--wpc-primary);
  background: transparent;
  font-size: 14px;
}

.signature-page__back > i {
  font-size: 20px;
}

.signature-page__title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
}

.signature-page__spacer {
  min-width: 56px;
}

.signature-page__body {
  flex: 1 1 auto;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 16px 12px 120px;
  box-sizing: border-box;
}

.signature-page__body--drawing {
  overflow: hidden;
}

.signature-page__content {
  border: 1px solid #e5eaf1;
  border-radius: 12px;
  background: #ffffff;
  overflow: hidden;
}

.signature-page__preview {
  margin-top: 16px;
  padding: 12px;
  border: 1px solid #dbe1ea;
  border-radius: 12px;
  background: #ffffff;
}

.signature-page__preview-title {
  margin-bottom: 10px;
  color: #1f2937;
  font-size: 14px;
  font-weight: 600;
}

.signature-page__preview img {
  display: block;
  width: 100%;
  height: auto;
  border-radius: 8px;
  background: #fff;
}

.signature-page__footer {
  flex: 0 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding: 12px 12px calc(12px + constant(safe-area-inset-bottom));
  padding: 12px 12px calc(12px + env(safe-area-inset-bottom));
  border-top: 1px solid #e8edf3;
  background: #ffffff;
}

.insurance-html-preview {
  padding: 14px;
  color: #374151;
  line-height: 1.8;
}

.insurance-html-empty {
  padding: 24px 16px;
  border: 1px dashed #d7dde6;
  border-radius: 10px;
  text-align: center;
  color: #8a94a6;
  background: #fafbfd;
}

.insurance-signature-card {
  margin-top: 16px;
}

.signature-card {
  padding: 12px;
  border: 1px solid #dbe1ea;
  border-radius: 10px;
  background: #fff;
}

.signature-contract-image {
  display: block;
  width: 100%;
  height: auto;
  background: #fff;
}

.signature-canvas {
  width: 100%;
  height: 220px;
  border: 1px dashed #7aa6d8;
  border-radius: 8px;
  background: #fff;
  touch-action: none;
}

.signature-preview {
  border: 1px dashed #7aa6d8;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.signature-tip {
  margin-top: 8px;
  color: #667085;
  font-size: 12px;
}

.page /deep/ .ivu-card-head {
  padding: 16px;
  border-bottom-color: #f0f2f5;
}

.page /deep/ .ivu-card-head p {
  height: auto;
  line-height: 1.4;
  padding-left: 10px;
  border-left: 3px solid var(--wpc-primary);
  font-size: 17px;
  color: var(--wpc-text);
}

.page /deep/ .ivu-card-body { padding: 18px 16px 8px; }
.page /deep/ .ivu-form-item { margin-bottom: 20px; }
.page /deep/ .ivu-form-item-label { padding-bottom: 8px; color: #475467; font-size: 14px; font-weight: 500; }
.page /deep/ .ivu-checkbox-group,
.page /deep/ .ivu-radio-group { display: flex; flex-wrap: wrap; gap: 8px 18px; }
.page /deep/ .ivu-checkbox-wrapper,
.page /deep/ .ivu-radio-wrapper {
  margin: 0;
  min-height: 32px;
  padding: 5px 0;
  border-radius: 0;
  background: transparent !important;
  color: #475467;
  border: 0 !important;
  box-shadow: none !important;
}

.page /deep/ .ivu-checkbox-group,
.page /deep/ .ivu-radio-group,
.consent-item,
.consent-item /deep/ .ivu-form-item-content,
.consent-item /deep/ .ivu-checkbox-wrapper {
  background: transparent !important;
  border: 0 !important;
  box-shadow: none !important;
}

.mobile-trigger__placeholder {
  color: #b8b8b8;
}

.mobile-trigger__value {
  color: #515a6e;
}

.mobile-trigger--disabled {
  background: #f5f7fa;
  cursor: not-allowed;
}

.consent-item {
  margin-top: 8px;
  margin-bottom: 6px !important;
  line-height: 1.8;
}

.consent-link {
  color: var(--wpc-primary);
}

.consent-modal p {
  margin-bottom: 10px;
  line-height: 1.7;
  color: #515a6e;
}

.consent-modal h3,
.consent-modal h4 {
  color: #1f2d3d;
}

.consent-modal h3 {
  margin-bottom: 14px;
  font-size: 18px;
}

.consent-modal h4 {
  margin: 14px 0 8px;
  font-size: 15px;
}

.consent-modal ol {
  padding-left: 20px;
  margin-bottom: 10px;
  color: #515a6e;
}

.consent-modal li {
  margin-bottom: 8px;
  line-height: 1.7;
}

.number-keyboard-actions {
  display: flex;
  align-items: center;
  gap: 2px;
  white-space: nowrap;
}

.number-keyboard-action {
  padding: 7px 6px;
  border: 0;
  color: var(--wpc-primary);
  background: transparent;
  font-size: 12px;
}

.consent-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 8px 0 12px;
}

.grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 16px;
}

.action-bar {
  position: fixed;
  z-index: 20;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 10px 16px 12px;
  padding-bottom: calc(12px + constant(safe-area-inset-bottom));
  padding-bottom: calc(12px + env(safe-area-inset-bottom));
  background: rgba(255, 255, 255, .96);
  border-top: 1px solid #edf0f4;
}

.action-bar > .ivu-btn {
  display: block;
  max-width: 596px;
  margin: 0 auto;
}

.action-bar__row {
  max-width: 596px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 10px;
}

@media (max-width: 640px) {
  .grid {
    grid-template-columns: 1fr;
  }

  .survey-progress {
    padding-right: 28px;
    padding-left: 28px;
  }

}
</style>



// WEBPACK FOOTER //
// src/views/IntakeSurvey.vue